import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import useSessionTimeout from '../hooks/useSessionTimeout';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { useWindowSize } from '../hooks/useWindowSize';
import { useDeviceLayout } from '../hooks/useDeviceLayout';
import { PanelLeft, PanelLeftClose, Wallet, CalendarDays, IndianRupee, TrendingUp, ArrowDownCircle, LogIn, LogOut } from 'lucide-react';
import { logoutAndRedirect } from '../utils/authLogout';
import { markAttendance, getOperatorPlQuota, reportIssue, getAttendancePage, getFuelLogsPage, getOperatorBookingsPage, getOperatorAssignments, getTodayAttendance, clockInAttendance, clockOutAttendance, updateOperatorLocation, secureFetch } from '../supabaseService';
import OperatorCompleteCard from '../components/booking/OperatorCompleteCard';
import OperatorContractHmrCard from '../components/booking/OperatorContractHmrCard';
import BookingFlowV2Card from '../components/booking/BookingFlowV2Card';
import { enrichBookingFromJoin, formatClockHMS, runningElapsedMs } from '../services/bookingFlowApi';
import { formatHoursHm } from '../utils/durationHours';
import { appendUniqueById } from '../utils/pagination';
import BookingFeedbackSummary from '../components/BookingFeedbackSummary';
import PushNotificationToggle from '../components/notifications/PushNotificationToggle';
import NotificationCenter from '../components/NotificationCenter';
import PhoneChangeModal from '../components/PhoneChangeModal';
import PortalAvatar from '../components/PortalAvatar';
import OperatorKycForm from '../components/OperatorKycForm';
import { loadProfilePhoto } from '../utils/profilePhotoStorage';
import { ATTACHMENT_LABEL_BY_ID } from '../utils/machineRegistrationConfig';

function getCurrentPayrollMonthStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getPreviousPayrollMonthStr(monthStr) {
  const year = Number(monthStr.slice(0, 4));
  const monthIndex = Number(monthStr.slice(5, 7));
  const d = new Date(year, monthIndex - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

async function getOperatorIssuesCount() {
  try {
    const data = await secureFetch('/api/operator/issues?limit=1');
    return Number(data.total ?? data.items?.length ?? 0);
  } catch (err) {
    if ((err?.message || '') === 'auth_required') return 0;
    return 0;
  }
}

function formatPayrollMonthLabel(monthStr) {
  const year = Number(monthStr.slice(0, 4));
  const monthIndex = Number(monthStr.slice(5, 7));
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return monthStr;
  return new Date(year, monthIndex - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatPayrollCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function computePayrollDaysWorked(attendanceSummary) {
  const summary = attendanceSummary || {};
  const present = Number(summary.present || 0);
  const halfday = Number(summary.halfday || 0);
  const paidLeave = Number(summary.paid_leave || 0);
  return present + (halfday * 0.5) + paidLeave;
}

function escapePayslipHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildPayslipHtml({
  payrollData,
  monthLabel,
  operatorName,
  labels,
  daysWorked,
  attendanceSummary,
}) {
  const summary = attendanceSummary || {};
  const row = (left, right) => `
    <tr>
      <td style="padding:8px 0;color:#444;">${escapePayslipHtml(left)}</td>
      <td style="padding:8px 0;text-align:right;font-weight:600;color:#111;">${escapePayslipHtml(right)}</td>
    </tr>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapePayslipHtml(labels.title)} — ${escapePayslipHtml(monthLabel)}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 32px; color: #111; }
    h1 { margin: 0 0 4px; font-size: 22px; }
    h2 { margin: 24px 0 8px; font-size: 14px; text-transform: uppercase; letter-spacing: 0.08em; color: #666; }
    .meta { color: #555; font-size: 13px; margin: 0 0 20px; }
    table { width: 100%; border-collapse: collapse; }
    .net { margin-top: 20px; padding: 14px 16px; background: #f3faf4; border: 1px solid #b7e4c7; border-radius: 8px; font-size: 18px; font-weight: 700; }
    .summary { margin-top: 16px; font-size: 13px; color: #444; line-height: 1.6; }
    footer { margin-top: 32px; font-size: 11px; color: #888; border-top: 1px solid #ddd; padding-top: 12px; }
    @media print { body { margin: 16px; } }
  </style>
</head>
<body>
  <h1>${escapePayslipHtml(labels.appName)} — ${escapePayslipHtml(labels.title)}</h1>
  <p class="meta">
    ${escapePayslipHtml(labels.period)}: ${escapePayslipHtml(monthLabel)}<br />
    ${escapePayslipHtml(labels.operator)}: ${escapePayslipHtml(operatorName)}
  </p>

  <h2>${escapePayslipHtml(labels.earnings)}</h2>
  <table>
    ${row(labels.monthlySalary, formatPayrollCurrency(payrollData.monthly_salary))}
    ${row(labels.dailyRate, formatPayrollCurrency(payrollData.daily_rate))}
    ${row(labels.daysWorked, String(daysWorked))}
    ${row(labels.gross, formatPayrollCurrency(payrollData.gross_payable))}
  </table>

  <h2>${escapePayslipHtml(labels.deductions)}</h2>
  <table>
    ${row(labels.advanceBalance, formatPayrollCurrency(payrollData.advance_balance))}
  </table>

  <div class="net">${escapePayslipHtml(labels.net)}: ${escapePayslipHtml(formatPayrollCurrency(payrollData.net_payable))}</div>

  <div class="summary">
    ${escapePayslipHtml(labels.present)}: ${summary.present ?? 0}
    | ${escapePayslipHtml(labels.halfDay)}: ${summary.halfday ?? 0}
    | ${escapePayslipHtml(labels.paidLeave)}: ${summary.paid_leave ?? 0}
    | ${escapePayslipHtml(labels.unpaidLeave)}: ${summary.unpaid_leave ?? 0}
    | ${escapePayslipHtml(labels.absent)}: ${summary.absent ?? 0}
  </div>

  <footer>${escapePayslipHtml(labels.generatedBy)}</footer>
</body>
</html>`;
}

function generatePayslipPDF(payrollData, monthStr, operatorName, t) {
  if (!payrollData) return;
  const attendanceSummary = payrollData.attendance_summary || {};
  const daysWorked = computePayrollDaysWorked(attendanceSummary);
  const monthLabel = formatPayrollMonthLabel(monthStr);
  const labels = {
    appName: t('appName'),
    title: t('payslipTitle'),
    period: t('payrollMonth'),
    operator: t('operatorRole'),
    earnings: t('payslipEarnings'),
    deductions: t('payslipDeductions'),
    daysWorked: t('payslipDaysWorked'),
    generatedBy: t('payslipGeneratedBy'),
    monthlySalary: t('payrollMonthlySalary'),
    dailyRate: t('payrollDailyRate'),
    gross: t('payrollGross'),
    advanceBalance: t('advanceBalance'),
    net: t('payrollNet'),
    present: t('presentLabel'),
    halfDay: t('halfDayLabel'),
    paidLeave: t('paidLeaveLabel'),
    unpaidLeave: t('unpaidLeaveLabel'),
    absent: t('absentLabel'),
  };
  const html = buildPayslipHtml({
    payrollData,
    monthLabel,
    operatorName: operatorName || payrollData.operator_name || '—',
    labels,
    daysWorked,
    attendanceSummary,
  });

  const printWin = window.open('', '_blank', 'noopener,noreferrer');
  if (!printWin) {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `payslip-${monthStr}.html`;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 500);
    return;
  }
  printWin.document.open();
  printWin.document.write(html);
  printWin.document.close();
  printWin.focus();
  window.setTimeout(() => {
    printWin.print();
  }, 300);
}

const payrollUi = {
  shell: { display: 'flex', flexDirection: 'column', gap: 14 },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: '#f0ead8', letterSpacing: '-0.02em' },
  headerSub: { margin: '4px 0 0', fontSize: 12, color: '#8896a8' },
  periodBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    background: 'rgba(201,168,76,0.12)',
    border: '1px solid rgba(201,168,76,0.28)',
    color: '#e2c97e',
    fontSize: 11,
    fontWeight: 700,
  },
  monthToggle: {
    display: 'inline-flex',
    padding: 3,
    borderRadius: 12,
    background: 'rgba(0,0,0,0.28)',
    border: '1px solid rgba(136,150,168,0.18)',
    gap: 4,
  },
  monthBtn: (active) => ({
    border: 'none',
    borderRadius: 9,
    padding: '8px 14px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    background: active ? 'linear-gradient(135deg, rgba(201,168,76,0.35), rgba(201,168,76,0.12))' : 'transparent',
    color: active ? '#f5e6b8' : '#8896a8',
    boxShadow: active ? '0 2px 12px rgba(201,168,76,0.15)' : 'none',
  }),
  heroCard: {
    borderRadius: 16,
    padding: '22px 20px',
    background: 'linear-gradient(135deg, rgba(34,197,94,0.18) 0%, rgba(15,32,64,0.95) 55%, rgba(10,22,40,1) 100%)',
    border: '1px solid rgba(74,222,128,0.28)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
  },
  heroLabel: {
    margin: 0,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(134,239,172,0.85)',
  },
  heroAmount: {
    margin: '8px 0 6px',
    fontSize: 36,
    fontWeight: 800,
    color: '#ecfdf5',
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
  },
  heroMeta: { margin: 0, fontSize: 11, color: '#8896a8', lineHeight: 1.5 },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
  },
  kpiCard: (accent) => ({
    borderRadius: 14,
    padding: '14px 14px 12px',
    background: 'linear-gradient(160deg, rgba(15,32,64,0.95), rgba(10,22,40,0.98))',
    border: `1px solid ${accent.border}`,
    minHeight: 92,
  }),
  kpiIcon: (accent) => ({
    width: 32,
    height: 32,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    background: accent.bg,
    color: accent.color,
  }),
  kpiLabel: { margin: 0, fontSize: 10, fontWeight: 600, color: '#8896a8', letterSpacing: '0.04em', textTransform: 'uppercase' },
  kpiValue: (color) => ({ margin: '6px 0 0', fontSize: 17, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }),
  sectionTitle: { margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#c9a84c', letterSpacing: '0.04em' },
  attendanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
    gap: 8,
  },
  attendanceChip: (color) => ({
    borderRadius: 12,
    padding: '12px 10px',
    textAlign: 'center',
    background: `${color}10`,
    border: `1px solid ${color}33`,
  }),
  attendanceCount: (color) => ({ margin: 0, fontSize: 22, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }),
  attendanceLabel: { margin: '4px 0 0', fontSize: 10, fontWeight: 600, color: '#8896a8', lineHeight: 1.3 },
  stateBox: {
    borderRadius: 14,
    padding: '28px 16px',
    textAlign: 'center',
    background: 'rgba(0,0,0,0.22)',
    border: '1px dashed rgba(136,150,168,0.25)',
  },
  skeleton: {
    borderRadius: 14,
    background: 'linear-gradient(90deg, rgba(136,150,168,0.08) 25%, rgba(136,150,168,0.16) 50%, rgba(136,150,168,0.08) 75%)',
    backgroundSize: '200% 100%',
    animation: 'payrollShimmer 1.2s ease-in-out infinite',
  },
  todayCard: {
    borderRadius: 16,
    padding: '22px 18px',
    textAlign: 'center',
    background: 'linear-gradient(160deg, rgba(15,32,64,0.95) 0%, rgba(10,22,40,0.98) 100%)',
    border: '1px solid rgba(201,168,76,0.28)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
  },
  goldActionBtn: (disabled) => ({
    border: 'none',
    borderRadius: 12,
    padding: '12px 28px',
    fontSize: 13,
    fontWeight: 800,
    cursor: disabled ? 'wait' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
    color: '#0a1628',
    boxShadow: '0 4px 16px rgba(201,168,76,0.25)',
    transition: 'all 0.15s ease',
  }),
  statusChipBtn: (color, selected) => ({
    borderRadius: 12,
    padding: '14px 10px',
    textAlign: 'center',
    cursor: 'pointer',
    background: `${color}${selected ? '22' : '10'}`,
    border: selected ? `2px solid ${color}` : `1px solid ${color}33`,
    boxShadow: selected ? `0 4px 14px ${color}33` : 'none',
    transition: 'all 0.15s ease',
  }),
  currentStatusCard: (color) => ({
    marginBottom: 16,
    padding: '18px 16px',
    borderRadius: 14,
    background: `linear-gradient(135deg, ${color}18 0%, rgba(10,22,40,0.95) 100%)`,
    border: `1px solid ${color}44`,
  }),
  currentStatusIcon: { fontSize: 48, margin: '0 0 8px', lineHeight: 1 },
  currentStatusTitle: (color) => ({ color, fontSize: 18, fontWeight: 800, margin: '0 0 4px' }),
  clockTimeBadge: (tone) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: tone.color,
    background: tone.bg,
    border: `1px solid ${tone.border}`,
  }),
  historyCard: {
    borderRadius: 16,
    padding: '16px 16px 14px',
    background: 'linear-gradient(160deg, rgba(15,32,64,0.95), rgba(10,22,40,0.98))',
    border: '1px solid rgba(201,168,76,0.28)',
    boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
  },
  tableWrap: { overflowX: 'auto', borderRadius: 12, border: '1px solid rgba(136,150,168,0.15)' },
  tableHeadCell: {
    padding: '11px 12px',
    textAlign: 'left',
    color: '#c9a84c',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(201,168,76,0.28)',
    background: 'rgba(201,168,76,0.08)',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    borderBottom: '1px solid rgba(136,150,168,0.12)',
    background: 'transparent',
  },
  tableRowAlt: {
    borderBottom: '1px solid rgba(136,150,168,0.12)',
    background: 'rgba(201,168,76,0.03)',
  },
  tableCell: {
    padding: '12px',
    fontSize: 12,
    color: '#e8e0d0',
    whiteSpace: 'nowrap',
  },
  footerNote: {
    margin: '10px 0 0',
    fontSize: 11,
    color: '#8896a8',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  loadMoreBtn: (disabled) => ({
    background: 'rgba(201,168,76,0.12)',
    border: '1px solid rgba(201,168,76,0.35)',
    color: '#e2c97e',
    borderRadius: 10,
    padding: '9px 16px',
    cursor: disabled ? 'wait' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    fontSize: 11,
    fontWeight: 700,
  }),
};

const NAV = [
  { id: 'home', icon: String.fromCodePoint(0x1F3E0), label: 'Home', i18nKey: 'home' },
  { id: 'attendance', icon: String.fromCodePoint(0x2705), label: 'Attendance', i18nKey: 'attendance' },
  { id: 'payroll', icon: String.fromCodePoint(0x1F4B0), label: 'Payroll', i18nKey: 'operatorPayrollTitle' },
  { id: 'machinelog', icon: String.fromCodePoint(0x1F69C), label: 'Machine Log', i18nKey: 'machineLog' },
  { id: 'fuel', icon: String.fromCodePoint(0x26FD), label: 'Fuel', i18nKey: 'fuel' },
  { id: 'alerts', icon: String.fromCodePoint(0x1F514), label: 'Alerts', i18nKey: 'alerts' },
  { id: 'notifications', icon: '🔔', label: 'Notifications', i18nKey: 'notificationCenter' },
  { id: 'daily', icon: String.fromCodePoint(0x1F4CB), label: 'Reports', i18nKey: 'reports' },
  { id: 'kyc', icon: '🪪', label: 'KYC', i18nKey: 'kycVerification' },
  { id: 'issues', icon: String.fromCodePoint(0x26A0), label: 'Issues', i18nKey: 'issues' },
];

const formatAttendanceStatus = (status, t) => {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'present') return t('presentLabel');
  if (normalized === 'halfday' || normalized === 'half_day') return t('halfDayLabel');
  if (normalized === 'absent') return t('absentLabel');
  if (normalized === 'paid_leave') return t('paidLeaveLabel');
  if (normalized === 'unpaid_leave') return t('unpaidLeaveLabel');
  if (status === 'Present' || status === 'Half Day' || status === 'Absent' || status === 'Paid Leave' || status === 'Unpaid Leave') {
    return formatAttendanceStatus(status.toLowerCase().replace(' ', '_'), t);
  }
  return status;
};

const mapAttendanceRow = (r) => ({
  id: r.id,
  date: r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-',
  dateKey: r.date ? String(r.date).slice(0, 10) : '',
  day: r.date ? new Date(r.date).toLocaleDateString('en-IN', { weekday: 'long' }) : '-',
  status: r.status || 'absent',
  statusKey: String(r.status || 'absent').toLowerCase(),
  checkIn: r.check_in || '-',
  checkOut: r.check_out || '-',
  hmr: r.hmr || 0,
  ownerProposedCheckIn: r.owner_proposed_check_in || null,
  correctionPending: Boolean(
    r.owner_proposed_check_in
    && !r.operator_correction_confirmed
    && !r.correction_rejected,
  ),
});

function formatAttendanceHmrDisplay(hmr) {
  const n = Number(hmr);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatHoursHm(n);
}

const attendanceStatusColor = (statusKey) => {
  if (statusKey === 'present' || statusKey === 'paid_leave') return '#4CAF50';
  if (statusKey === 'halfday') return '#FF9800';
  if (statusKey === 'unpaid_leave') return '#e94560';
  return '#8896a8';
};

const buildIssueTypes = (t, { includeSiteOther = false, fuelShort = false } = {}) => {
  const items = [
    { id: 'mechanical', icon: String.fromCodePoint(0x1F527), label: t('issueMechanical') },
    { id: 'fuel', icon: String.fromCodePoint(0x26FD), label: fuelShort ? t('issueFuelShort') : t('issueFuel') },
    { id: 'accident', icon: String.fromCodePoint(0x1F6A8), label: t('issueAccident') },
    { id: 'electrical', icon: String.fromCodePoint(0x26A1), label: t('issueElectrical') },
  ];
  if (includeSiteOther) {
    items.push(
      { id: 'site', icon: String.fromCodePoint(0x1F4CD), label: t('issueSite') },
      { id: 'other', icon: String.fromCodePoint(0x1F4CB), label: t('issueOther') },
    );
  }
  return items;
};

const OPERATOR_TAB_ORDER = NAV.map((n) => n.id);

const OP_CHART_GOLD = '#C9A84C';
const OP_CHART_GOLD_LIGHT = '#f5e6b8';
const OP_CHART_GRID = 'rgba(201, 168, 76, 0.15)';
const OP_CHART_AXIS = '#8896a8';
const OP_CHART_HEIGHT = 280;

function formatOperatorChartHmr(value) {
  return formatHoursHm(value);
}

function useOperatorChartWidth() {
  const [width, setWidth] = useState(320);
  const ref = React.useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect?.width || 0);
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width: Math.max(width, 280) };
}

function OperatorAnalyticsTooltip({
  active,
  payload,
  label,
  presentDaysLabel,
  jobsLabel,
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      style={{
        background: 'linear-gradient(145deg, #0f2040, #0D1B2A)',
        border: '1px solid rgba(201, 168, 76, 0.4)',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, color: 'rgba(201,168,76,0.8)', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: '16px', fontWeight: 700, color: '#c9a84c' }}>
        {formatOperatorChartHmr(row.hmr)}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#8896a8' }}>
        {presentDaysLabel}: {row.present_days ?? 0}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#8896a8' }}>
        {jobsLabel}: {row.jobs ?? 0}
      </p>
    </div>
  );
}

const OperatorBookingSiteLocation = ({ booking, t, navigateProminent = false }) => {
  if (!booking) return null;
  const siteAddress = String(booking.site_address || '').trim();
  const sitePincode = String(booking.site_pincode || '').trim();
  const siteLat = Number(booking.site_lat);
  const siteLng = Number(booking.site_lng);
  const hasSiteCoords = Number.isFinite(siteLat) && Number.isFinite(siteLng);
  if (!siteAddress && !sitePincode && !hasSiteCoords) return null;

  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(siteLat)},${encodeURIComponent(siteLng)}`;

  return (
    <div style={{
      marginTop: navigateProminent ? '10px' : '8px',
      padding: navigateProminent ? '12px' : '10px',
      background: 'rgba(201,168,76,0.06)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: '8px',
    }}>
      {siteAddress && (
        <p style={{ color: '#e8e0d0', fontSize: navigateProminent ? '12px' : '11px', fontWeight: 600, margin: '0 0 8px', lineHeight: 1.4, wordBreak: 'break-word' }}>
          {String.fromCodePoint(0x1F4CD)} {t('siteAddressLabel')}: {siteAddress}
        </p>
      )}
      {sitePincode && (
        <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 10px' }}>
          {t('sitePincodeLabel')}: <span style={{ color: '#e8e0d0', fontWeight: 600 }}>{sitePincode}</span>
        </p>
      )}
      {hasSiteCoords && (
        <a
          href={navUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
            padding: navigateProminent ? '14px 16px' : '11px 14px',
            background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
            color: '#0a1628',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: navigateProminent ? '14px' : '13px',
            textDecoration: 'none',
            minHeight: navigateProminent ? '48px' : '44px',
            lineHeight: 1.3,
          }}
        >
          {t('navigateToSite')}
        </a>
      )}
    </div>
  );
};

/** Milestone / unit-rate bookings: log completed units for client co-sign. */
function OperatorMilestoneUnitsCard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unitsById, setUnitsById] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [successById, setSuccessById] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/operator/milestone-bookings');
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load milestone bookings');
      }
      setBookings(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load milestone bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLog = async (booking) => {
    if (!booking?.id || busyId) return;
    const raw = unitsById[booking.id];
    const units = Number(raw);
    if (!Number.isFinite(units) || units <= 0) {
      setError('Enter units completed (number > 0)');
      return;
    }
    setBusyId(booking.id);
    setError('');
    try {
      const result = await secureFetch(
        `/api/operator/bookings/${encodeURIComponent(booking.id)}/milestone/log-units`,
        {
          method: 'POST',
          body: JSON.stringify({ unitsCompleted: units }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to log units');
      }
      setSuccessById((prev) => ({ ...prev, [booking.id]: 'Units logged — client will confirm' }));
      setUnitsById((prev) => ({ ...prev, [booking.id]: '' }));
      setBookings((prev) => prev.map((b) => (
        b.id === booking.id
          ? {
            ...b,
            total_units_completed: result.totalUnitsCompleted ?? b.total_units_completed,
          }
          : b
      )));
    } catch (err) {
      setError(err?.message || 'Failed to log units');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      background: 'rgba(14, 40, 64, 0.9)',
      border: '1px solid rgba(201,168,76,0.3)',
      borderRadius: '12px',
      padding: '14px',
      marginBottom: '16px',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: '#c9a84c', fontSize: '13px', fontWeight: 700 }}>
          Milestone unit logging
        </h3>
        <button
          type="button"
          onClick={load}
          style={{
            background: 'transparent',
            border: '1px solid rgba(201,168,76,0.35)',
            color: '#c9a84c',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p style={{ margin: '0 0 10px', color: '#8896a8', fontSize: '11px' }}>
        Log completed units (foot / cubic metre / trip / load). Client must confirm joint measurement.
      </p>
      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}
      {bookings.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>No active milestone bookings assigned.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {bookings.map((b) => {
            const unit = b.booking_unit_label || 'units';
            const completed = Number(b.total_units_completed || 0);
            const estimated = Number(b.total_units_estimated || 0);
            const rate = Number(b.rate_per_unit || 0);
            return (
              <div
                key={b.id}
                style={{
                  background: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: '10px',
                  padding: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <span style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 700 }}>
                    {b.booking_ref || b.id}
                  </span>
                  <span style={{ color: '#8896a8', fontSize: '10px' }}>
                    {b.machines?.machine_id || b.machines?.name || 'Machine'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
                    <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Unit</p>
                    <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{unit}</p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
                    <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Rate / unit</p>
                    <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                      ₹{rate.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px', gridColumn: '1 / -1' }}>
                    <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Progress</p>
                    <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                      {completed.toLocaleString('en-IN')} / {estimated.toLocaleString('en-IN')} {unit}
                    </p>
                  </div>
                </div>
                <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '8px' }}>
                  Units completed
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitsById[b.id] ?? ''}
                    onChange={(e) => setUnitsById((prev) => ({ ...prev, [b.id]: e.target.value }))}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: '4px',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(201,168,76,0.3)',
                      background: 'rgba(0,0,0,0.35)',
                      color: '#e8e0d0',
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
                <button
                  type="button"
                  disabled={busyId === b.id}
                  onClick={() => handleLog(b)}
                  style={{
                    width: '100%',
                    padding: '11px',
                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                    color: '#030810',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: busyId === b.id ? 'wait' : 'pointer',
                    opacity: busyId === b.id ? 0.7 : 1,
                  }}
                >
                  {busyId === b.id ? 'Logging…' : 'Log Units Today'}
                </button>
                {successById[b.id] && (
                  <p style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 700, margin: '8px 0 0', textAlign: 'center' }}>
                    {successById[b.id]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Phase 13: strata depth logging for active site work bookings. */
function OperatorSiteWorkStrataCard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formById, setFormById] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [successById, setSuccessById] = useState({});
  const [todayById, setTodayById] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/operator/site-work');
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load site work bookings');
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setBookings(items);
      const todayKey = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
      const nextToday = {};
      items.forEach((b) => {
        const logs = Array.isArray(b.strata_logs) ? b.strata_logs : [];
        const todays = logs.filter((l) => String(l.log_date || '').slice(0, 10) === todayKey);
        const depth = todays.reduce(
          (sum, l) => sum + Math.max(0, Number(l.depth_to || 0) - Number(l.depth_from || 0)),
          0,
        );
        const earned = todays.reduce((sum, l) => sum + Number(l.amount_due || 0), 0);
        nextToday[b.id] = { depth, earned, count: todays.length };
      });
      setTodayById(nextToday);
    } catch (err) {
      setError(err?.message || 'Could not load site work bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateForm = (bookingId, patch) => {
    setFormById((prev) => ({
      ...prev,
      [bookingId]: { ...(prev[bookingId] || {}), ...patch },
    }));
  };

  const readPhotoAsDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => reject(new Error('Failed to read photo'));
    reader.readAsDataURL(file);
  });

  const handleLog = async (booking) => {
    if (!booking?.id || busyId) return;
    const form = formById[booking.id] || {};
    const depthFrom = Number(form.depthFrom);
    const depthTo = Number(form.depthTo);
    const strataType = String(form.strataType || '').trim();
    if (!Number.isFinite(depthFrom) || !Number.isFinite(depthTo) || depthTo <= depthFrom) {
      setError('Enter depth_from and depth_to (depth_to > depth_from)');
      return;
    }
    if (!['soft', 'murram', 'rock'].includes(strataType)) {
      setError('Select strata type (soft / murram / rock)');
      return;
    }
    setBusyId(booking.id);
    setError('');
    try {
      const photoUrl = await readPhotoAsDataUrl(form.photoFile || null);
      const result = await secureFetch(
        `/api/operator/site-work/${encodeURIComponent(booking.id)}/log-strata`,
        {
          method: 'POST',
          body: JSON.stringify({
            depthFrom,
            depthTo,
            strataType,
            photoUrl,
          }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to log strata depth');
      }
      setSuccessById((prev) => ({
        ...prev,
        [booking.id]: `Logged ${depthFrom}–${depthTo} (${strataType}) — ₹${Number(result?.log?.amount_due || 0).toLocaleString('en-IN')}`,
      }));
      setFormById((prev) => ({
        ...prev,
        [booking.id]: { depthFrom: '', depthTo: '', strataType: '', photoFile: null },
      }));
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to log strata depth');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      background: 'rgba(14, 40, 64, 0.9)',
      border: '1px solid rgba(201,168,76,0.3)',
      borderRadius: '12px',
      padding: '14px',
      marginBottom: '16px',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: '#c9a84c', fontSize: '13px', fontWeight: 700 }}>
          Site work — strata depth log
        </h3>
        <button
          type="button"
          onClick={load}
          style={{
            background: 'transparent',
            border: '1px solid rgba(201,168,76,0.35)',
            color: '#c9a84c',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p style={{ margin: '0 0 10px', color: '#8896a8', fontSize: '11px' }}>
        Log depth_from → depth_to with strata type. Client confirms within 24 hours.
      </p>
      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}
      {bookings.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>No active site work bookings assigned.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {bookings.map((b) => {
            const form = formById[b.id] || {};
            const today = todayById[b.id] || { depth: 0, earned: 0, count: 0 };
            const unit = b.billing_basis === 'per_meter' ? 'm' : 'ft';
            return (
              <div
                key={b.id}
                style={{
                  background: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: '10px',
                  padding: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <span style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 700 }}>
                    {b.booking_ref || b.id}
                  </span>
                  <span style={{ color: '#8896a8', fontSize: '10px' }}>
                    {String(b.site_work_type || '').replace(/_/g, ' ')} · {b.machines?.machine_id || b.machines?.name || 'Machine'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
                    <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Today depth</p>
                    <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                      {Number(today.depth || 0).toLocaleString('en-IN')} {unit}
                    </p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
                    <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Today amount</p>
                    <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                      ₹{Number(today.earned || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <label style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    depth_from
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.depthFrom ?? ''}
                      onChange={(e) => updateForm(b.id, { depthFrom: e.target.value })}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: '4px',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(201,168,76,0.3)',
                        background: 'rgba(0,0,0,0.35)',
                        color: '#e8e0d0',
                        boxSizing: 'border-box',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    depth_to
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.depthTo ?? ''}
                      onChange={(e) => updateForm(b.id, { depthTo: e.target.value })}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: '4px',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(201,168,76,0.3)',
                        background: 'rgba(0,0,0,0.35)',
                        color: '#e8e0d0',
                        boxSizing: 'border-box',
                      }}
                    />
                  </label>
                </div>
                <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '8px' }}>
                  strata_type
                  <select
                    value={form.strataType || ''}
                    onChange={(e) => updateForm(b.id, { strataType: e.target.value })}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: '4px',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(201,168,76,0.3)',
                      background: 'rgba(0,0,0,0.35)',
                      color: '#e8e0d0',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Select…</option>
                    <option value="soft">soft</option>
                    <option value="murram">murram</option>
                    <option value="rock">rock</option>
                  </select>
                </label>
                <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
                  Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => updateForm(b.id, { photoFile: e.target.files?.[0] || null })}
                    style={{ display: 'block', width: '100%', marginTop: '4px', color: '#e8e0d0', fontSize: '11px' }}
                  />
                </label>
                <button
                  type="button"
                  disabled={busyId === b.id}
                  onClick={() => handleLog(b)}
                  style={{
                    width: '100%',
                    padding: '11px',
                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                    color: '#030810',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: busyId === b.id ? 'wait' : 'pointer',
                    opacity: busyId === b.id ? 0.7 : 1,
                  }}
                >
                  {busyId === b.id ? 'Logging…' : 'Log Strata Depth'}
                </button>
                {successById[b.id] && (
                  <p style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 700, margin: '8px 0 0', textAlign: 'center' }}>
                    {successById[b.id]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Phase 11: metered attachment connect / disconnect on active assignment. */
function OperatorAttachmentMeterCard({ booking }) {
  const attachmentId = booking?.selected_attachment
    ? String(booking.selected_attachment).trim()
    : '';
  const attachmentName = ATTACHMENT_LABEL_BY_ID[attachmentId] || attachmentId;

  const [openConnect, setOpenConnect] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());

  const loadSession = useCallback(async () => {
    if (!booking?.id || !attachmentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(
        `/api/operator/bookings/${encodeURIComponent(booking.id)}/attachment/session`,
      );
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load attachment session');
      }
      setOpenConnect(data?.openConnect || null);
      setTodayLogs(Array.isArray(data?.todayLogs) ? data.todayLogs : []);
    } catch (err) {
      setError(err?.message || 'Could not load attachment session');
      setOpenConnect(null);
      setTodayLogs([]);
    } finally {
      setLoading(false);
    }
  }, [booking?.id, attachmentId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!openConnect?.event_at) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [openConnect?.event_at]);

  if (!booking?.id || !attachmentId) return null;

  const connectedSinceMs = openConnect?.event_at
    ? new Date(openConnect.event_at).getTime()
    : null;
  const runningHours = Number.isFinite(connectedSinceMs) && connectedSinceMs > 0
    ? Math.max(0, (nowMs - connectedSinceMs) / 3600000)
    : 0;
  const connectedSinceLabel = Number.isFinite(connectedSinceMs)
    ? new Date(connectedSinceMs).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '—';

  const postEvent = async (eventType) => {
    if (!booking?.id || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await secureFetch(
        `/api/operator/bookings/${encodeURIComponent(booking.id)}/attachment/${eventType}`,
        {
          method: 'POST',
          body: JSON.stringify({ attachmentId }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || `Failed to ${eventType} attachment`);
      }
      if (eventType === 'connect') {
        setOpenConnect(result.event || null);
      } else {
        setOpenConnect(null);
        if (result.usageLog) {
          setTodayLogs((prev) => [result.usageLog, ...(prev || []).filter((r) => r.id !== result.usageLog.id)]);
        }
      }
      await loadSession();
    } catch (err) {
      setError(err?.message || `Failed to ${eventType} attachment`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: '10px',
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid rgba(201,168,76,0.28)',
      background: 'rgba(201,168,76,0.06)',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: 0 }}>
          Attachment metering
        </p>
        <button
          type="button"
          onClick={loadSession}
          style={{
            background: 'transparent',
            border: '1px solid rgba(201,168,76,0.35)',
            color: '#c9a84c',
            borderRadius: '8px',
            padding: '3px 8px',
            fontSize: '10px',
            cursor: 'pointer',
          }}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: '0 0 8px' }}>
        {String.fromCodePoint(0x1F527)} {attachmentName}
      </p>

      {openConnect ? (
        <div style={{
          marginBottom: '10px',
          padding: '10px',
          borderRadius: '8px',
          background: 'rgba(0,0,0,0.28)',
          border: '1px solid rgba(233,69,96,0.25)',
        }}
        >
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>Connected since</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: '0 0 6px' }}>
            {connectedSinceLabel}
          </p>
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>Running hours</p>
          <p style={{ color: '#c9a84c', fontSize: '14px', fontWeight: 800, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
            {runningHours.toFixed(2)} hr
          </p>
        </div>
      ) : (
        <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 10px' }}>
          No open connect — tap Connect when attachment is fitted.
        </p>
      )}

      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}

      {!openConnect ? (
        <button
          type="button"
          disabled={busy || loading}
          onClick={() => postEvent('connect')}
          style={{
            width: '100%',
            padding: '11px',
            background: 'linear-gradient(135deg, #a07830, #e2c97e)',
            color: '#030810',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 800,
            fontSize: '13px',
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy || loading ? 0.7 : 1,
          }}
        >
          {busy ? 'Connecting…' : 'Connect Attachment'}
        </button>
      ) : (
        <button
          type="button"
          disabled={busy || loading}
          onClick={() => postEvent('disconnect')}
          style={{
            width: '100%',
            padding: '11px',
            background: 'rgba(80,20,30,0.55)',
            border: '1px solid rgba(233,69,96,0.45)',
            color: '#ff9aa8',
            borderRadius: '8px',
            fontWeight: 800,
            fontSize: '13px',
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy || loading ? 0.7 : 1,
          }}
        >
          {busy ? 'Disconnecting…' : 'Disconnect Attachment'}
        </button>
      )}

      <div style={{ marginTop: '12px' }}>
        <p style={{ color: '#8896a8', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>
          Today&apos;s usage log
        </p>
        {todayLogs.length === 0 ? (
          <p style={{ color: '#556070', fontSize: '11px', margin: 0 }}>No sessions logged today.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {todayLogs.map((log) => (
              <div
                key={log.id}
                style={{
                  background: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(201,168,76,0.15)',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: '#e8e0d0', fontSize: '11px' }}>
                  Raw {Number(log.raw_hours || 0).toFixed(2)} hr
                </span>
                <span style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 800 }}>
                  Billed {Number(log.billed_hours || 0).toFixed(2)} hr
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const OperatorAssignmentPanel = ({ booking, assignmentSite, t, onRefresh, loading, error, showLiveMeter = true }) => {
  const [locationSubmitting, setLocationSubmitting] = useState(null);
  const [locationMessage, setLocationMessage] = useState('');

  const handleOperatorLocationUpdate = (status) => {
    if (!booking?.id || locationSubmitting) return;
    setLocationMessage('');

    const sendUpdate = async (lat, lng) => {
      setLocationSubmitting(status);
      try {
        const result = await updateOperatorLocation(booking.id, status, lat, lng);
        if (result.success) {
          setLocationMessage(status === 'on_the_way' ? 'On my way — client notified' : 'Arrival confirmed — client notified');
          onRefresh?.();
        } else {
          setLocationMessage(result.error || 'Location update failed');
        }
      } catch (err) {
        setLocationMessage(err?.message || 'Location update failed');
      } finally {
        setLocationSubmitting(null);
      }
    };

    const confirmWithoutGps = () => {
      if (window.confirm('GPS unavailable or denied. Continue without location?')) {
        sendUpdate(null, null);
      }
    };

    if (!navigator.geolocation) {
      confirmWithoutGps();
      return;
    }

    setLocationSubmitting(status);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        sendUpdate(position.coords.latitude, position.coords.longitude);
      },
      () => {
        setLocationSubmitting(null);
        confirmWithoutGps();
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  if (loading) {
    return <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loadingAssignment')}</p>;
  }
  if (error) {
    return <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{error}</p>;
  }
  if (!booking) {
    return (
      <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(201,168,76,0.15)' }}>
        <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 6px' }}>{t('noActiveAssignment')}</p>
        <p style={{ color: '#556070', fontSize: '11px', margin: 0 }}>
          {t('noActiveAssignmentHint')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
        {[
          { icon: String.fromCodePoint(0x1F69C), label: t('machine'), val: booking.machines?.machine_id || '—' },
          { icon: String.fromCodePoint(0x1F522), label: t('bookingLabel'), val: booking.booking_ref || '—' },
          { icon: String.fromCodePoint(0x1F477), label: t('client'), val: 'Client' },
          { icon: String.fromCodePoint(0x1F4CD), label: t('workSiteLabel'), val: assignmentSite },
        ].map((d, i) => (
          <div key={i} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
            <span style={{ fontSize: '16px', marginBottom: '4px', display: 'block' }}>{d.icon}</span>
            <p style={{ color: '#556070', fontSize: '9px', margin: '0 0 2px', letterSpacing: '0.5px' }}>{d.label.toUpperCase()}</p>
            <p style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '11px', margin: 0, wordBreak: 'break-word' }}>{d.val}</p>
          </div>
        ))}
      </div>
      {String(booking.status) === 'Dispatched' && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={Boolean(locationSubmitting)}
              onClick={() => handleOperatorLocationUpdate('on_the_way')}
              style={{
                flex: 1,
                minWidth: '140px',
                background: 'rgba(201,168,76,0.12)',
                border: '1px solid rgba(201,168,76,0.45)',
                color: '#C9A84C',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: locationSubmitting ? 'wait' : 'pointer',
                opacity: locationSubmitting && locationSubmitting !== 'on_the_way' ? 0.6 : 1,
              }}
            >
              {locationSubmitting === 'on_the_way' ? 'Sending GPS…' : `${String.fromCodePoint(0x1F4CD)} On My Way`}
            </button>
            <button
              type="button"
              disabled={Boolean(locationSubmitting)}
              onClick={() => handleOperatorLocationUpdate('arrived')}
              style={{
                flex: 1,
                minWidth: '140px',
                background: 'rgba(76,175,80,0.12)',
                border: '1px solid rgba(76,175,80,0.45)',
                color: '#4CAF50',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: locationSubmitting ? 'wait' : 'pointer',
                opacity: locationSubmitting && locationSubmitting !== 'arrived' ? 0.6 : 1,
              }}
            >
              {locationSubmitting === 'arrived' ? 'Sending GPS…' : `${String.fromCodePoint(0x2705)} I've Arrived`}
            </button>
          </div>
          {locationMessage && (
            <p style={{ color: '#C9A84C', fontSize: '11px', margin: '8px 0 0', textAlign: 'center' }}>
              {locationMessage}
            </p>
          )}
        </div>
      )}
      {showLiveMeter && <BookingFlowV2Card booking={booking} viewOnly onRefresh={onRefresh} />}
      <OperatorBookingSiteLocation booking={booking} t={t} navigateProminent />
      {(booking.selected_attachment || booking.site_location || booking.work_description) && (
        <div style={{
          marginTop: '8px',
          padding: '10px',
          background: 'rgba(0,0,0,0.25)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: '8px',
        }}
        >
          {booking.selected_attachment && (
            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 6px', wordBreak: 'break-word' }}>
              {String.fromCodePoint(0x1F527)} {t('bookingAttachmentLabel')}: {booking.selected_attachment}
            </p>
          )}
          {booking.site_location && (
            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 6px', wordBreak: 'break-word' }}>
              {String.fromCodePoint(0x1F4CD)} {t('bookingSiteLabel')}: {booking.site_location}
            </p>
          )}
          {booking.work_description && (
            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: 0, wordBreak: 'break-word' }}>
              {String.fromCodePoint(0x1F4CB)} {t('bookingWorkLabel')}: {booking.work_description}
            </p>
          )}
        </div>
      )}
      {booking.selected_attachment && (
        <OperatorAttachmentMeterCard booking={booking} />
      )}
      <OperatorCompleteCard booking={booking} onDone={onRefresh} />
    </>
  );
};

const OperatorDashboard = () => {
  useSessionTimeout();
  const navigate = useNavigate();
  const { t } = useLanguage(); // eslint-disable-line
  const navItems = NAV.map((item) => ({ ...item, label: item.i18nKey ? t(item.i18nKey) : item.label }));
  const { isMobile, gridCols } = useWindowSize();
  const layoutMode = useDeviceLayout();
  const useMobileNav = layoutMode === 'phone';
  const sidebarOverlay = layoutMode === 'tablet';
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    setSidebarOpen(!sidebarOverlay);
  }, [sidebarOverlay]);

  const [activeTab, setActiveTab] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [clockSubmitting, setClockSubmitting] = useState(false);
  const [attendanceActionError, setAttendanceActionError] = useState('');
  const [attendanceActionWarning, setAttendanceActionWarning] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [plQuota, setPlQuota] = useState(null);
  const [fuelLevel, setFuelLevel] = useState(72);
  const [fuelCapacity, setFuelCapacity] = useState(0);
  const [fuelCapacityReady, setFuelCapacityReady] = useState(false);
  const [fuelNote, setFuelNote] = useState('');
  const [fuelSubmitting, setFuelSubmitting] = useState(false);
  const [fuelSuccess, setFuelSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [logs, setLogs] = useState([]);
  const [showPhoneChangeModal, setShowPhoneChangeModal] = useState(false);

  const handleOperatorPhoneChanged = (newPhone) => {
    setLoggedInUser((prev) => {
      const next = { ...prev, phone: newPhone };
      localStorage.setItem('developmentexpress_user', JSON.stringify(next));
      return next;
    });
  };

  const [showFuelModal, setShowFuelModal] = useState(false);
  const [fuelLevelBeforeModal, setFuelLevelBeforeModal] = useState(0);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [issueNote, setIssueNote] = useState('');
  const [issueHistory, setIssueHistory] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState(null);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [fuelRows, setFuelRows] = useState([]);
  const [attendanceOffset, setAttendanceOffset] = useState(0);
  const [attendanceHasMore, setAttendanceHasMore] = useState(false);
  const [attendanceLoadingMore, setAttendanceLoadingMore] = useState(false);
  const [fuelOffset, setFuelOffset] = useState(0);
  const [fuelHasMore, setFuelHasMore] = useState(false);
  const [fuelLoadingMore, setFuelLoadingMore] = useState(false);
  const [clientFeedbackBookings, setClientFeedbackBookings] = useState([]);
  const [feedbackOffset, setFeedbackOffset] = useState(0);
  const [feedbackHasMore, setFeedbackHasMore] = useState(false);
  const [feedbackLoadingMore, setFeedbackLoadingMore] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [operatorAnalytics, setOperatorAnalytics] = useState(null);
  const [opAnalyticsLoading, setOpAnalyticsLoading] = useState(false);
  const [opAnalyticsError, setOpAnalyticsError] = useState(null);
  const [opAnalyticsMonths, setOpAnalyticsMonths] = useState(6);
  const [payrollData, setPayrollData] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(() => getCurrentPayrollMonthStr());
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [operatorAssignments, setOperatorAssignments] = useState([]);
  const [operatorIssuesCount, setOperatorIssuesCount] = useState(0);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [assignmentError, setAssignmentError] = useState('');
  const [operatorKycStatus, setOperatorKycStatus] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(() => JSON.parse(localStorage.getItem('developmentexpress_user') || '{}'));
  const [operatorPhoto, setOperatorPhoto] = useState('');
  const operatorHardwareBackFromPopRef = React.useRef(false);
  const operatorActiveTabRef = React.useRef(activeTab);
  const operatorHardwareBackModalsRef = React.useRef({
    showFuelModal: false,
    showIssueModal: false,
    menuOpen: false,
  });

  operatorActiveTabRef.current = activeTab;
  operatorHardwareBackModalsRef.current = {
    showFuelModal,
    showIssueModal,
    menuOpen,
    sidebarOpen,
    sidebarOverlay,
    fuelLevelBeforeModal,
  };

  const handleLogout = () => {
    logoutAndRedirect(navigate);
  };

  useEffect(() => {
    setOperatorPhoto(loadProfilePhoto(loggedInUser?.id, 'operator') || '');
  }, [loggedInUser?.id]);

  useEffect(() => {
    const loadOperatorKycStatus = async () => {
      try {
        const data = await secureFetch('/api/operator/kyc');
        setOperatorKycStatus(data.status || data.kyc?.status || 'not_submitted');
      } catch (err) {
        if ((err?.message || '') === 'auth_required') return;
        // soft gate — ignore load errors
      }
    };
    loadOperatorKycStatus();
  }, []);

  const syncFuelFromAssignments = (items) => {
    const assignment = items[0] || null;
    if (assignment == null) return;

    const fuelLvl = assignment.fuelLevel ?? assignment.machines?.fuel_level;
    const fuelCap = assignment.fuelCapacityLitres ?? assignment.machines?.fuel_capacity_litres;

    if (fuelLvl != null) setFuelLevel(Number(fuelLvl));
    if (fuelCap != null && Number(fuelCap) > 0) setFuelCapacity(Number(fuelCap));
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const refreshTodayAttendance = async () => {
    const result = await getTodayAttendance();
    const record = result.record
      ? { ...result.record, date: result.record.date || result.date }
      : null;
    setTodayAttendance(record);
    setAttendance(record?.status || null);
    return record;
  };

  const refreshAttendanceHistory = async () => {
    const result = await getAttendancePage({ limit: 30, offset: 0 });
    const rows = result.items || [];
    setAttendanceRows(rows.map(mapAttendanceRow));
    setAttendanceHasMore(Boolean(result.hasMore));
    setAttendanceOffset(result.nextOffset || 0);
  };

  const loadPlQuota = async () => {
    const result = await getOperatorPlQuota({ year: new Date().getFullYear() });
    if (result.success && result.quota) {
      setPlQuota(result.quota);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    if (!user?.id) return;

    refreshTodayAttendance();
    refreshAttendanceHistory();
    loadPlQuota();

    getFuelLogsPage({ limit: 50, offset: 0 }).then((result) => {
      const rows = result.items || [];
      if (rows.length > 0) {
        setFuelRows(rows.map((r) => ({
          date: r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-',
          time: r.created_at ? new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-',
          level: r.fuel_level || 0,
          change: 0,
          note: r.note || t('fuelUpdateDefault'),
          by: t('operatorByLabel'),
        })));
      }
      setFuelHasMore(Boolean(result.hasMore));
      setFuelOffset(result.nextOffset || 0);
    });

    getOperatorBookingsPage({ limit: 50, offset: 0 }).then((result) => {
      setClientFeedbackBookings(result.items || []);
      setFeedbackHasMore(Boolean(result.hasMore));
      setFeedbackOffset(result.nextOffset || 0);
    });

    const loadAssignments = () => {
      getOperatorAssignments().then((result) => {
        const items = (result.items || []).map(enrichBookingFromJoin);
        setOperatorAssignments(items);
        setActiveAssignment(items[0] || null);
        syncFuelFromAssignments(items);
        setAssignmentError(result.error || '');
        setAssignmentsLoading(false);
      });
    };

    loadAssignments();
    getOperatorIssuesCount().then((count) => setOperatorIssuesCount(count));

    const assignmentPoll = setInterval(loadAssignments, 5000);

    return () => clearInterval(assignmentPoll);
  }, []);

  useEffect(() => {
    if (activeTab === 'home' || activeTab === 'machinelog') {
      getOperatorAssignments().then((result) => {
        const items = (result.items || []).map(enrichBookingFromJoin);
        setOperatorAssignments(items);
        setActiveAssignment(items[0] || null);
        syncFuelFromAssignments(items);
        setAssignmentError(result.error || '');
        setAssignmentsLoading(false);
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeAssignment?.machines) {
      const machine = activeAssignment.machines;
      if (machine.fuel_level != null) {
        setFuelLevel(Number(machine.fuel_level));
      }
    }
    setFuelCapacity((prev) => {
      const next = activeAssignment?.machines?.fuel_capacity_litres;
      return next != null ? Number(next) : prev;
    });
  }, [activeAssignment]);

  useEffect(() => {
    if (operatorHardwareBackFromPopRef.current) {
      operatorHardwareBackFromPopRef.current = false;
      return;
    }
    window.history.pushState(
      { tab: activeTab },
      '',
      `?tab=${activeTab}`,
    );
  }, [activeTab]);

  useEffect(() => {
    const restoreCurrentTabInHistory = () => {
      const tab = operatorActiveTabRef.current;
      window.history.pushState({ tab }, '', `?tab=${tab}`);
    };

    const onPopState = (event) => {
      const modals = operatorHardwareBackModalsRef.current;
      if (modals.sidebarOpen && modals.sidebarOverlay) {
        setSidebarOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showFuelModal) {
        setFuelLevel(modals.fuelLevelBeforeModal);
        setFuelNote('');
        setShowFuelModal(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showIssueModal) {
        setShowIssueModal(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.menuOpen) {
        setMenuOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (event.state && event.state.tab) {
        operatorHardwareBackFromPopRef.current = true;
        setActiveTab(event.state.tab);
      } else {
        operatorHardwareBackFromPopRef.current = true;
        setActiveTab(OPERATOR_TAB_ORDER[0]);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const fuelCapacityRef = React.useRef(fuelCapacity);
  fuelCapacityRef.current = fuelCapacity;

  async function loadMyMachine() {
    if (fuelCapacityRef.current !== 0) {
      setFuelCapacityReady(true);
      return;
    }
    try {
      const data = await secureFetch('/api/operator/my-machine');
      if (!data?.machine) {
        setFuelCapacityReady(true);
        return;
      }
      if (fuelCapacityRef.current !== 0) {
        setFuelCapacityReady(true);
        return;
      }
      const machine = data.machine;
      setFuelCapacity(Number(machine.fuel_capacity_litres) || 0);
      setFuelLevel((prev) => Number(machine.fuel_level) || prev);
      setFuelCapacityReady(true);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      console.error(err);
      setFuelCapacityReady(true);
    }
  }

  useEffect(() => {
    loadMyMachine();
  }, []);

  useEffect(() => {
    if (activeTab === 'fuel' && fuelCapacity === 0) {
      loadMyMachine();
    }
  }, [activeTab, fuelCapacity]);

  async function loadDailyReport() {
    setReportLoading(true);
    setReportError(null);
    try {
      const data = await secureFetch(
        `/api/operator/daily-report?month=${reportMonth}&year=${reportYear}`,
      );
      setReportData(data);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setReportError(t('failedToLoadReport'));
    } finally {
      setReportLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'daily') return;
    loadDailyReport();
  }, [activeTab, reportMonth, reportYear]);

  async function loadOperatorAnalytics() {
    setOpAnalyticsLoading(true);
    setOpAnalyticsError(null);
    try {
      const data = await secureFetch(`/api/operator/analytics?months=${opAnalyticsMonths}`);
      setOperatorAnalytics(data);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setOpAnalyticsError(t('failedToLoad'));
    } finally {
      setOpAnalyticsLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'daily') return;
    loadOperatorAnalytics();
  }, [activeTab, opAnalyticsMonths]);

  async function loadPayrollPreview() {
    setPayrollLoading(true);
    setPayrollError(null);
    try {
      const data = await secureFetch(
        `/api/operator/payroll/preview?month=${encodeURIComponent(payrollMonth)}`,
      );
      setPayrollData(data);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPayrollData(null);
      setPayrollError(err.message || t('failedToLoad'));
    } finally {
      setPayrollLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'payroll') return;
    loadPayrollPreview();
  }, [activeTab, payrollMonth]);

  useEffect(() => {
    if (activeTab !== 'attendance') return;
    loadPlQuota();
  }, [activeTab]);

  const payrollCurrentMonth = getCurrentPayrollMonthStr();
  const payrollPrevMonth = getPreviousPayrollMonthStr(payrollCurrentMonth);
  const payrollAttendanceSummary = payrollData?.attendance_summary || {};

  const opAnalyticsTrendSeries = useMemo(
    () => (Array.isArray(operatorAnalytics?.monthly_trend) ? operatorAnalytics.monthly_trend : []),
    [operatorAnalytics],
  );
  const opAnalyticsMaxHmr = useMemo(
    () => Math.max(...opAnalyticsTrendSeries.map((r) => Number(r.hmr || 0)), 1),
    [opAnalyticsTrendSeries],
  );
  const { ref: opChartWrapRef, width: opChartWidth } = useOperatorChartWidth();

  async function loadIssueHistory() {
    setIssuesLoading(true);
    setIssuesError(null);
    try {
      const data = await secureFetch('/api/operator/issues?limit=20');
      setIssueHistory(data.items || []);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setIssuesError(t('failedToLoadIssues'));
    } finally {
      setIssuesLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'issues') return;
    loadIssueHistory();
  }, [activeTab]);

  const refreshActiveAssignment = () => {
    getOperatorAssignments().then((result) => {
      const items = (result.items || []).map(enrichBookingFromJoin);
      setOperatorAssignments(items);
      setActiveAssignment(items[0] || null);
      syncFuelFromAssignments(items);
      setAssignmentError(result.error || '');
      setAssignmentsLoading(false);
    });
  };

  const assignmentRunning = Boolean(
    activeAssignment?.machine_started_at
    && ['Dispatched', 'Extension_Pending'].includes(String(activeAssignment?.status || '')),
  );
  const assignmentMachineLabel = activeAssignment?.machines?.machine_id
    || activeAssignment?.machines?.name
    || (assignmentsLoading ? '…' : '—');
  const assignmentSite = activeAssignment?.site_address
    || activeAssignment?.location
    || '—';
  const operatorDisplayName = loggedInUser?.name || '—';
  const operatorExpLabel = loggedInUser?.experience_years
    ? `${loggedInUser.experience_years} Years`
    : '—';
  const totalHmrDisplay = '—';
  const machineRunning = assignmentRunning;
  const assignmentPaused = Boolean(activeAssignment?.pause_started_at);
  const assignmentStatusLabel = assignmentPaused
    ? 'Paused'
    : machineRunning
      ? t('running')
      : (activeAssignment ? t('assignedStatus') : t('stopped'));
  const assignmentStatusColor = assignmentPaused ? '#FF9800' : machineRunning ? '#4CAF50' : '#FF9800';
  const todayHmrHours = assignmentRunning && activeAssignment?.machine_started_at
    ? (runningElapsedMs(activeAssignment, currentTime.getTime()) / 3600000)
    : null;
  const todayHmrDisplay = todayHmrHours != null
    ? formatHoursHm(todayHmrHours)
    : (assignmentsLoading ? '…' : '—');
  const machineStartLabel = activeAssignment?.machine_started_at
    ? new Date(activeAssignment.machine_started_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  const loadMoreAttendance = async () => {
    if (!attendanceHasMore || attendanceLoadingMore) return;
    setAttendanceLoadingMore(true);
    const result = await getAttendancePage({ limit: 30, offset: attendanceOffset });
    const rows = result.items || [];
    if (rows.length > 0) {
      const mapped = rows.map(mapAttendanceRow);
      setAttendanceRows((prev) => appendUniqueById(prev, mapped));
      setAttendanceOffset(result.nextOffset || attendanceOffset);
      setAttendanceHasMore(Boolean(result.hasMore));
    } else {
      setAttendanceHasMore(false);
    }
    setAttendanceLoadingMore(false);
  };
  const loadMoreFuelLogs = async () => {
    if (!fuelHasMore || fuelLoadingMore) return;
    setFuelLoadingMore(true);
    const result = await getFuelLogsPage({ limit: 50, offset: fuelOffset });
    const rows = result.items || [];
    if (rows.length > 0) {
      const mapped = rows.map((r) => ({
        id: r.id,
        date: r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-',
        time: r.created_at ? new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-',
        level: r.fuel_level || 0,
        change: 0,
        note: r.note || t('fuelUpdateDefault'),
        by: t('operatorByLabel'),
      }));
      setFuelRows((prev) => appendUniqueById(prev, mapped));
      setFuelOffset(result.nextOffset || fuelOffset);
      setFuelHasMore(Boolean(result.hasMore));
    } else {
      setFuelHasMore(false);
    }
    setFuelLoadingMore(false);
  };
  const loadMoreClientFeedback = async () => {
    if (!feedbackHasMore || feedbackLoadingMore) return;
    setFeedbackLoadingMore(true);
    const result = await getOperatorBookingsPage({ limit: 50, offset: feedbackOffset });
    if ((result.items || []).length > 0) {
      setClientFeedbackBookings((prev) => appendUniqueById(prev, result.items));
      setFeedbackOffset(result.nextOffset || feedbackOffset);
      setFeedbackHasMore(Boolean(result.hasMore));
    } else {
      setFeedbackHasMore(false);
    }
    setFeedbackLoadingMore(false);
  };
  const clientRatingAverage = clientFeedbackBookings.length > 0
    ? (clientFeedbackBookings.reduce((sum, b) => sum + Number(b.client_operator_rating || 0), 0) / clientFeedbackBookings.length).toFixed(1)
    : '—';

  const handleAttendance = async (type) => {
    setAttendanceActionError('');
    setAttendanceActionWarning('');
    const result = await markAttendance({
      date: new Date().toISOString().split('T')[0],
      status: type,
    });
    if (!result.success) {
      setAttendanceActionError(t('failedToMarkAttendance'));
      return;
    }
    const savedStatus = result.auto_converted ? result.status : type;
    if (result.auto_converted) {
      setAttendanceActionWarning(t('plQuotaExhausted'));
    }
    setAttendance(savedStatus);
    setLogs((prev) => [{
      type: 'attendance',
      time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      note: `${t('attendanceLogNote')} - ${formatAttendanceStatus(savedStatus, t)}`,
      fuel: fuelLevel,
    }, ...prev]);
    await refreshTodayAttendance();
    await refreshAttendanceHistory();
    await loadPlQuota();
  };

  const handleClockIn = async () => {
    setAttendanceActionError('');
    setClockSubmitting(true);
    try {
      const result = await clockInAttendance();
      if (!result.success) {
        setAttendanceActionError(result.error || t('failedToClockIn'));
        return;
      }
      await refreshTodayAttendance();
      await refreshAttendanceHistory();
    } finally {
      setClockSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    setAttendanceActionError('');
    setClockSubmitting(true);
    try {
      const result = await clockOutAttendance();
      if (!result.success) {
        setAttendanceActionError(result.error || t('failedToClockOut'));
        return;
      }
      await refreshTodayAttendance();
      await refreshAttendanceHistory();
    } finally {
      setClockSubmitting(false);
    }
  };

  const handleConfirmCorrection = async (dateKey, confirmed) => {
    if (!dateKey || correctionSubmitting) return;
    setAttendanceActionError('');
    setCorrectionSubmitting(true);
    try {
      await secureFetch(
        `/api/operator/attendance/${encodeURIComponent(dateKey)}/confirm-correction`,
        {
          method: 'POST',
          body: JSON.stringify(confirmed ? { confirmed: true } : { rejected: true }),
        },
      );
      await refreshTodayAttendance();
      await refreshAttendanceHistory();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAttendanceActionError(err?.message || t('actionFailed') || 'Failed to respond to correction');
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const handleFuelUpdate = async () => {
    if (fuelSubmitting) return;
    setFuelSubmitting(true);
    try {
      const now = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
      const note = fuelNote || t('fuelUpdated');
      await secureFetch('/api/operator/fuel-log', {
        method: 'POST',
        body: JSON.stringify({
          fuelLevel: Number(fuelLevel),
          note,
          machine_id: activeAssignment?.machine_id || null,
        }),
      });
      setLogs((prev) => [{ type: 'fuel', time: now, note, fuel: fuelLevel }, ...prev]);
      setFuelNote('');
      setFuelSuccess('✅ Fuel updated');
      await new Promise((resolve) => setTimeout(resolve, 800));
      setShowFuelModal(false);
      setTimeout(() => setFuelSuccess(''), 3000);
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      console.error(e);
      alert(e.message || 'Fuel update failed');
    } finally {
      setFuelSubmitting(false);
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const handlePause = async () => {
    if (!activeAssignment?.id) return;
    const pausedAt = new Date().toISOString();
    setActiveAssignment((prev) => (prev ? { ...prev, pause_started_at: pausedAt } : prev));
    try {
      await secureFetch(`/api/operator/booking/${activeAssignment.id}/pause`, { method: 'POST' });
      refreshActiveAssignment();
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      setActiveAssignment((prev) => (
        prev?.pause_started_at === pausedAt ? { ...prev, pause_started_at: null } : prev
      ));
      console.error(e);
      alert(e.message || 'Pause failed');
    }
  };

  const handleResume = async () => {
    if (!activeAssignment?.id || !activeAssignment.pause_started_at) return;
    const pauseStartedAt = activeAssignment.pause_started_at;
    const priorTotalPaused = Number(activeAssignment.total_paused_ms || 0);
    setActiveAssignment((prev) => (prev ? { ...prev, pause_started_at: null } : prev));
    try {
      const body = await secureFetch(`/api/operator/booking/${activeAssignment.id}/resume`, {
        method: 'POST',
      });
      const totalPausedMs = body.total_paused_ms ?? (
        priorTotalPaused + (Date.now() - new Date(pauseStartedAt).getTime())
      );
      setActiveAssignment((prev) => (
        prev ? { ...prev, pause_started_at: null, total_paused_ms: totalPausedMs } : prev
      ));
      refreshActiveAssignment();
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      setActiveAssignment((prev) => (
        prev?.pause_started_at == null ? { ...prev, pause_started_at: pauseStartedAt } : prev
      ));
      console.error(e);
      alert('Resume failed');
    }
  };

  const pauseTimerControls = assignmentRunning ? (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      {activeAssignment?.pause_started_at ? (
        <button
          type="button"
          style={{
            background: 'rgba(76,175,80,0.15)',
            border: '1px solid rgba(76,175,80,0.4)',
            color: '#4CAF50',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 12,
            cursor: 'pointer',
            flex: 1,
          }}
          onClick={handleResume}
        >
          ▶ {t('resumeTimer')}
        </button>
      ) : (
        <button
          type="button"
          style={{
            background: 'rgba(255,152,0,0.15)',
            border: '1px solid rgba(255,152,0,0.4)',
            color: '#FF9800',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 12,
            cursor: 'pointer',
            flex: 1,
          }}
          onClick={handlePause}
        >
          ⏸ {t('pauseTimer')}
        </button>
      )}
    </div>
  ) : null;

  const handleReportIssue = async () => {
    const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    const now = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    setLogs(prev => [{ type: 'issue', time: now, note: '[' + issueType + '] ' + issueNote, fuel: fuelLevel }, ...prev]);
    if (user?.id) await reportIssue({ operator_id: user.id, issue_type: issueType, description: issueNote, status: 'Pending', created_at: new Date().toISOString() });
    setIssueType(''); setIssueNote(''); setShowIssueModal(false);
    alert(t('issueReportedSuccess'));
    loadIssueHistory();
  };

  const logIcon = (type) => {
    const map = { start: { icon: String.fromCodePoint(0x1F7E2), color: '#4CAF50' }, stop: { icon: String.fromCodePoint(0x1F534), color: '#e94560' }, fuel: { icon: String.fromCodePoint(0x26FD), color: '#FF9800' }, issue: { icon: String.fromCodePoint(0x26A0), color: '#e94560' }, attendance: { icon: String.fromCodePoint(0x2705), color: '#4CAF50' } };
    return map[type] || { icon: String.fromCodePoint(0x1F4CB), color: '#c9a84c' };
  };

  function fuelDisplay(fuelPct, capacityLitres) {
    if (capacityLitres > 0) {
      const litres = Math.round(
        (fuelPct / 100) * capacityLitres,
      );
      return `${litres}L / ${capacityLitres}L`;
    }
    return `${fuelPct}%`;
  }

  const fuelPct = fuelCapacity > 0
    ? Math.round((fuelLevel / fuelCapacity) * 100)
    : fuelLevel;
  const fuelColor = fuelPct > 50 ? '#4CAF50' : fuelPct > 25 ? '#FF9800' : '#e94560';
  const currentNavItem = navItems.find(n => n.id === activeTab);

  const localAlerts = [];
  if (operatorKycStatus !== 'approved' && operatorKycStatus !== 'rejected') {
    localAlerts.push({
      id: 'local_kyc',
      icon: '🪪',
      msg: operatorKycStatus === 'pending' ? t('kycPendingHint') : t('kycNotSubmittedHint'),
      color: '#FF9800',
      link: 'kyc',
    });
  }
  const pushEnabled = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}')?.pushNotificationsEnabled;
  if (!pushEnabled) {
    localAlerts.push({
      id: 'local_push',
      icon: '🔔',
      msg: t('pushEnablePrompt'),
      color: '#FF9800',
      link: 'alerts',
    });
  }
  const alertsBadgeCount = (activeAssignment ? 1 : 0) + localAlerts.length;

  const navItemsWithBadge = navItems.map((item) => ({
    ...item,
    ...(item.id === 'alerts' && alertsBadgeCount > 0
      ? { badge: alertsBadgeCount > 99 ? '99+' : alertsBadgeCount }
      : {}),
  }));

  const renderNavBadge = (badge) => {
    if (!badge) return null;
    return (
      <span
        style={{
          marginLeft: 'auto',
          minWidth: '18px',
          height: '18px',
          padding: '0 5px',
          borderRadius: '999px',
          background: '#e94560',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 800,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {badge}
      </span>
    );
  };

  return (
    <div
      className={useMobileNav ? 'portal-dashboard-shell--phone' : ''}
      style={useMobileNav ? s.containerMobile : s.container}
    >

      {/* --- MOBILE LAYOUT --- */}
      {useMobileNav && (
        <>
          {/* Top Bar */}
          <div style={s.topBar}>
            <div style={s.topBarLeft}>
              <div style={s.topLogoCircle}>DE</div>
              <div>
                <p style={s.topTitle}>{t('appName')}</p>
                <p style={s.topSub}>{t('operatorPortal')}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LanguageSelector compact={true} />
              <button style={s.hamburger} onClick={() => setMenuOpen(true)}>
                {String.fromCodePoint(0x2630)}
              </button>
            </div>
          </div>

          {/* Drawer Overlay */}
          {menuOpen && (
            <div style={s.overlay} onClick={() => setMenuOpen(false)}>
              <div style={s.drawer} onClick={e => e.stopPropagation()}>
                {/* Drawer Header */}
                <div style={s.drawerHeader}>
                  <div style={s.drawerAvatar}>{operatorDisplayName.charAt(0)}</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '14px', margin: 0 }}>{operatorDisplayName}</p>
                    <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{String.fromCodePoint(0x2B50)} {clientRatingAverage} {String.fromCodePoint(0x00B7)} {operatorExpLabel}</p>
                  </div>
                  <button style={s.closeBtn} onClick={() => setMenuOpen(false)} aria-label={t('closeMenuAria')}>{String.fromCodePoint(0x2715)}</button>
                </div>

                {/* Machine Status */}
                <div style={s.drawerMachineStatus}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: assignmentStatusColor, display: 'inline-block' }}></span>
                    <span style={{ color: '#c9a84c', fontSize: '12px', fontWeight: '700' }}>{assignmentMachineLabel}</span>
                    <span style={{ color: assignmentStatusColor, fontSize: '11px' }}>{assignmentStatusLabel}</span>
                  </div>
                  <span style={{ color: fuelColor, fontSize: '12px', fontWeight: '700' }}>{String.fromCodePoint(0x26FD)} {fuelDisplay(fuelLevel, fuelCapacity)}</span>
                </div>

                <div style={{ height: '1px', background: 'rgba(201,168,76,0.15)' }} />

                {/* Nav Items */}
                <div style={{ padding: '8px', flex: 1, overflowY: 'auto' }}>
                  {navItemsWithBadge.map(item => (
                    <button key={item.id}
                      style={activeTab === item.id ? s.drawerNavActive : s.drawerNav}
                      onClick={() => { setActiveTab(item.id); setMenuOpen(false); }}>
                      <span style={{ fontSize: '20px', minWidth: '28px' }}>{item.icon}</span>
                      <span style={{ flex: 1, fontSize: '14px', minWidth: 0 }}>{item.label}</span>
                      {renderNavBadge(item.badge)}
                      {!item.badge && activeTab === item.id && <span style={{ color: '#c9a84c', fontSize: '12px' }}>{String.fromCodePoint(0x25B6)}</span>}
                    </button>
                  ))}
                </div>

                <div style={{ height: '1px', background: 'rgba(201,168,76,0.15)' }} />

                {/* Logout */}
                <div style={{ padding: '12px 16px' }}>
                  <button style={s.drawerLogout} onClick={handleLogout}>
                    🚪 {t('logout')}
                  </button>
                </div>
              </div>
            </div>
          )}

        </>
      )}

      {/* --- DESKTOP SIDEBAR --- */}
      {!useMobileNav && sidebarOverlay && (
        <>
          {sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                zIndex: 30,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(1px)',
              }}
            />
          )}
          <div style={{
            ...s.sidebar,
            position: 'fixed',
            left: 0, top: 0,
            zIndex: 40,
            height: '100svh',
            maxHeight: '100svh',
            transform: sidebarOpen
              ? 'translateX(0)'
              : 'translateX(-100%)',
            transition: 'transform 300ms ease',
            pointerEvents: sidebarOpen ? 'auto' : 'none',
          }}>
            <div style={s.sidebarHeader}>
              <div style={s.sidebarHeaderBrand}>
                <div style={s.logoCircle}>DE</div>
                <div>
                  <p style={s.logoTitle}>{t('appName')}</p>
                  <p style={s.logoSub}>{t('operatorPortal')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                style={s.sidebarToggleBtn}
                aria-label="Close sidebar"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
            <div style={s.divider} />
            {navItemsWithBadge.map(item => (
              <button key={item.id} style={activeTab === item.id ? s.navActive : s.nav} onClick={() => { setActiveTab(item.id); if (sidebarOverlay) setSidebarOpen(false); }}>
                <span style={{ fontSize: '16px' }}>{item.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                {renderNavBadge(item.badge)}
              </button>
            ))}
            <div style={s.divider} />
            <div style={s.machineStatusBox}>
              <p style={s.msLabel}>{String.fromCodePoint(0x1F69C)} {assignmentMachineLabel}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ ...s.statusDot, background: assignmentStatusColor }}></span>
                <span style={{ color: assignmentStatusColor, fontSize: '11px', fontWeight: '700' }}>{assignmentStatusLabel}</span>
              </div>
              <p style={{ ...s.msLabel, color: fuelColor }}>{String.fromCodePoint(0x26FD)} Fuel: {fuelDisplay(fuelLevel, fuelCapacity)}</p>
            </div>
            <button style={s.logoutBtn} onClick={handleLogout}>🚪 {t('logout')}</button>
            <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
          </div>
        </>
      )}

      {!useMobileNav && !sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          style={s.sidebarOpenFab}
          aria-label="Open sidebar"
        >
          <PanelLeft size={16} />
        </button>
      )}

      {!useMobileNav && !sidebarOverlay && sidebarOpen && (
        <div style={s.sidebar}>
          <div style={s.sidebarHeader}>
            <div style={s.sidebarHeaderBrand}>
              <div style={s.logoCircle}>DE</div>
              <div>
                <p style={s.logoTitle}>{t('appName')}</p>
                <p style={s.logoSub}>{t('operatorPortal')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              style={s.sidebarToggleBtn}
              aria-label="Close sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </div>
          <div style={s.divider} />
          {navItemsWithBadge.map(item => (
            <button key={item.id} style={activeTab === item.id ? s.navActive : s.nav} onClick={() => { setActiveTab(item.id); if (sidebarOverlay) setSidebarOpen(false); }}>
              <span style={{ fontSize: '16px' }}>{item.icon}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
              {renderNavBadge(item.badge)}
            </button>
          ))}
          <div style={s.divider} />
          <div style={s.machineStatusBox}>
            <p style={s.msLabel}>{String.fromCodePoint(0x1F69C)} {assignmentMachineLabel}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ ...s.statusDot, background: assignmentStatusColor }}></span>
              <span style={{ color: assignmentStatusColor, fontSize: '11px', fontWeight: '700' }}>{assignmentStatusLabel}</span>
            </div>
            <p style={{ ...s.msLabel, color: fuelColor }}>{String.fromCodePoint(0x26FD)} Fuel: {fuelDisplay(fuelLevel, fuelCapacity)}</p>
          </div>
          <button style={s.logoutBtn} onClick={handleLogout}>🚪 {t('logout')}</button>
          <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
        </div>
      )}

      {/* --- MAIN CONTENT --- */}
      <div className={useMobileNav ? 'portal-dashboard-main-wrap--phone' : 'portal-dashboard-scroll-shell'} style={useMobileNav ? undefined : s.scrollShell}>
      <div
        className={useMobileNav ? 'portal-dashboard-main--phone' : 'portal-dashboard-main--locked'}
        style={{
          ...(useMobileNav ? s.mainMobile : s.main),
          ...(useMobileNav
            ? { padding: 'calc(66px + env(safe-area-inset-top, 0px)) 12px calc(16px + env(safe-area-inset-bottom, 0px))' }
            : isMobile
              ? { padding: '20px 16px' }
              : { padding: !sidebarOpen ? '25px 25px 25px 60px' : '25px' }),
        }}
      >

        {/* Header */}
        <div style={{ ...s.header, marginBottom: isMobile ? '14px' : '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <h2 style={{ ...s.pageTitle, fontSize: isMobile ? '15px' : '20px' }}>
                {currentNavItem?.icon} {currentNavItem?.label}
              </h2>
              {!useMobileNav && (
              <p style={s.pageDate}>
                {String.fromCodePoint(0x1F4C5)} {currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })} {String.fromCodePoint(0x00B7)} {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
              )}
            </div>
          </div>
          {!useMobileNav && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <LanguageSelector compact={true} />
              <div style={s.opBadge}>
                <PortalAvatar userId={loggedInUser?.id} name={operatorDisplayName} photoUrl={operatorPhoto} size={32} />
                {String.fromCodePoint(0x1F527)} {operatorDisplayName}
              </div>
            </div>
          )}
        </div>

        {/* --- HOME TAB --- */}
        {activeTab === 'home' && (
          <div>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 6px' }}>Registered Mobile: {loggedInUser.phone}</p>
            <button type="button" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', marginBottom: '12px' }} onClick={() => setShowPhoneChangeModal(true)}>Change Mobile Number</button>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 2, 4), gap: '10px', marginBottom: '14px', minWidth: 0 }}>
              {[
                { icon: String.fromCodePoint(0x23F1), val: todayHmrDisplay, label: t('hmrToday'), color: '#c9a84c' },
                { icon: String.fromCodePoint(0x1F4CA), val: totalHmrDisplay === '—' ? '—' : formatHoursHm(totalHmrDisplay), label: t('hmrTotal'), color: '#c9a84c' },
                { icon: String.fromCodePoint(0x26FD), val: fuelDisplay(fuelLevel, fuelCapacity), label: t('fuelLevel'), color: fuelColor },
                { icon: String.fromCodePoint(0x2B50), val: clientRatingAverage, label: t('myClientRating'), color: '#FFD700' },
              ].map((c, i) => (
                <div key={i} style={{ ...s.statCard, minWidth: 0, minHeight: 44 }}>
                  <div style={s.statIconWrap}><span style={{ fontSize: '22px' }}>{c.icon}</span></div>
                  <h3 style={{ ...s.statVal, color: c.color }}>{c.val}</h3>
                  <p style={{ ...s.statLabel, fontSize: '12px' }}>{c.label}</p>
                </div>
              ))}
            </div>

            {/* Work Status */}
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 2, 4), gap: '10px', marginBottom: '14px', minWidth: 0 }}>
              {[
                { icon: String.fromCodePoint(0x2705), val: operatorAssignments.filter((a) => a.status === 'Completed').length, label: 'Completed', color: '#4CAF50' },
                { icon: String.fromCodePoint(0x23F3), val: operatorAssignments.filter((a) => a.status === 'Dispatched' || a.status === 'Assigned').length, label: 'Pending', color: '#C9A84C' },
                { icon: String.fromCodePoint(0x1F504), val: operatorAssignments.filter((a) => a.status === 'Active').length, label: 'Active', color: '#e8e0d0' },
                { icon: String.fromCodePoint(0x26A0), val: operatorIssuesCount, label: 'Issues', color: '#e94560' },
              ].map((c) => (
                <div key={c.label} style={{ ...s.statCard, borderTop: '3px solid #C9A84C', minWidth: 0, minHeight: 44 }}>
                  <div style={s.statIconWrap}><span style={{ fontSize: '22px' }}>{c.icon}</span></div>
                  <h3 style={{ ...s.statVal, color: c.color }}>{c.val}</h3>
                  <p style={{ ...s.statLabel, fontSize: '12px' }}>{c.label}</p>
                </div>
              ))}
            </div>

            {/* Machine Status Banner */}
            <div style={{ ...s.machineBanner, borderColor: assignmentRunning ? '#4CAF50' : '#FF9800', background: assignmentRunning ? 'rgba(76,175,80,0.08)' : 'rgba(255,152,0,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: assignmentRunning ? '#4CAF50' : '#FF9800', display: 'inline-block', boxShadow: `0 0 8px ${assignmentRunning ? '#4CAF50' : '#FF9800'}` }}></span>
                <span style={{ color: assignmentRunning ? '#4CAF50' : '#FF9800', fontWeight: '700', fontSize: '13px' }}>
                  {assignmentMachineLabel} {String.fromCodePoint(0x00B7)} {assignmentStatusLabel}
                </span>
              </div>
              <span style={{ color: '#8896a8', fontSize: '11px' }}>{String.fromCodePoint(0x1F4CD)} {assignmentSite}</span>
            </div>

            {pauseTimerControls}

            {/* Today's Assignment */}
            <div style={{ ...s.card }}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F3AF)} Today's Assignment</h3>
              <OperatorAssignmentPanel
                booking={activeAssignment}
                assignmentSite={assignmentSite}
                t={t}
                onRefresh={refreshActiveAssignment}
                loading={assignmentsLoading}
                error={assignmentError}
              />
            </div>

            <OperatorContractHmrCard />

            <OperatorMilestoneUnitsCard />

            <OperatorSiteWorkStrataCard />

            {/* Client Ratings from bookings */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x2B50)} {t('clientRatingsTitle')}</h3>
              {clientFeedbackBookings.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('noClientFeedbackYet')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {clientFeedbackBookings.map((b) => (
                    <div key={b.id} style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '10px', padding: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 700 }}>{b.booking_ref || b.id}</span>
                        <span style={{ color: '#8896a8', fontSize: '10px' }}>
                          {b.machines?.machine_id || '—'} · Client
                        </span>
                      </div>
                      <BookingFeedbackSummary booking={b} />
                      <OperatorBookingSiteLocation booking={b} t={t} />
                    </div>
                  ))}
                  {feedbackHasMore && (
                    <button
                      type="button"
                      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: feedbackLoadingMore ? 'wait' : 'pointer' }}
                      disabled={feedbackLoadingMore}
                      onClick={loadMoreClientFeedback}
                    >
                      {feedbackLoadingMore ? t('loading') : t('loadMoreBookings')}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x26A1)} {t('quickActionsTitle')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 2, 4), gap: '8px', minWidth: 0 }}>
                {[
                  { icon: String.fromCodePoint(0x2705), label: t('attendance'), color: '#4CAF50', action: () => setActiveTab('attendance') },
                  { icon: String.fromCodePoint(0x1F69C), label: activeAssignment ? t('quickWork') : t('quickBooking'), color: '#c9a84c', action: () => setActiveTab('machinelog') },
                  { icon: String.fromCodePoint(0x26FD), label: t('fuel'), color: '#FF9800', action: () => { setFuelLevelBeforeModal(fuelLevel); setShowFuelModal(true); } },
                  { icon: String.fromCodePoint(0x26A0), label: t('quickIssueBtn'), color: '#e94560', action: () => setShowIssueModal(true) },
                ].map((btn, i) => (
                  <button key={i} style={{ ...s.actionBtn, borderColor: btn.color + '60', color: btn.color, background: btn.color + '12', minHeight: 44, minWidth: 0 }} onClick={btn.action}>
                    <span style={{ fontSize: '24px', display: 'block', marginBottom: '6px' }}>{btn.icon}</span>
                    <span style={{ fontSize: '12px', fontWeight: '700' }}>{btn.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Fuel Gauge */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x26FD)} {t('fuelLevelSensor')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={s.fuelGaugeWrap}>
                  <div style={{ ...s.fuelGaugeFill, height: `${fuelCapacityReady ? (fuelCapacity > 0 ? Math.round((fuelLevel / fuelCapacity) * 100) : fuelLevel) : 0}%`, background: `linear-gradient(180deg, ${fuelColor}, ${fuelColor}88)` }} />
                  <span style={s.fuelGaugePct}>{fuelCapacityReady ? fuelDisplay(fuelLevel, fuelCapacity) : '...'}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: fuelColor, fontSize: '32px', fontWeight: '900', margin: '0 0 4px' }}>{fuelCapacityReady ? fuelDisplay(fuelLevel, fuelCapacity) : '...'}</p>
                  <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 12px' }}>
                    {fuelPct > 50 ? String.fromCodePoint(0x2705) + ' ' + t('fuelOk') : fuelPct > 25 ? String.fromCodePoint(0x26A0) + ' ' + t('fuelLow') : String.fromCodePoint(0x1F6A8) + ' ' + t('fuelCritical')}
                  </p>
                  <div style={s.fuelProgressBg}>
                    <div style={{ ...s.fuelProgressFill, width: `${fuelCapacityReady ? (fuelCapacity > 0 ? Math.round((fuelLevel / fuelCapacity) * 100) : fuelLevel) : 0}%`, background: `linear-gradient(90deg, ${fuelColor}, ${fuelColor}88)` }} />
                  </div>
                  <button style={s.fuelUpdateBtn} onClick={() => { setFuelLevelBeforeModal(fuelLevel); setShowFuelModal(true); }}>
                    {String.fromCodePoint(0x26FD)} {t('updateFuel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- ATTENDANCE TAB --- */}
        {activeTab === 'attendance' && (
          <div style={payrollUi.shell}>
            <div style={payrollUi.todayCard}>
              <div style={payrollUi.headerRow}>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <h3 style={payrollUi.headerTitle}>{t('attendance')}</h3>
                  <p style={payrollUi.headerSub}>{t('markTodayAttendance')}</p>
                </div>
                <span style={payrollUi.periodBadge}>
                  <CalendarDays size={14} />
                  {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>

              {attendanceActionError && (
                <p style={{ color: '#e94560', fontSize: 12, margin: '12px 0 0', textAlign: 'center' }}>{attendanceActionError}</p>
              )}
              {attendanceActionWarning && (
                <p style={{ color: '#FF9800', fontSize: 12, margin: '12px 0 0', textAlign: 'center', fontWeight: 700 }}>{attendanceActionWarning}</p>
              )}

              <p style={{ ...payrollUi.sectionTitle, marginTop: 18, textAlign: 'left' }}>
                {t('attendanceClockSection')}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                {!todayAttendance?.check_in ? (
                  <button
                    type="button"
                    style={payrollUi.goldActionBtn(clockSubmitting)}
                    disabled={clockSubmitting}
                    onClick={handleClockIn}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <LogIn size={16} />
                      {clockSubmitting ? t('loading') : t('clockInLabel')}
                    </span>
                  </button>
                ) : !todayAttendance?.check_out ? (
                  <button
                    type="button"
                    style={payrollUi.goldActionBtn(clockSubmitting)}
                    disabled={clockSubmitting}
                    onClick={handleClockOut}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <LogOut size={16} />
                      {clockSubmitting ? t('loading') : t('clockOutLabel')}
                    </span>
                  </button>
                ) : null}
              </div>
              {(todayAttendance?.check_in || todayAttendance?.check_out) && (
                <div style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginBottom: 18,
                }}
                >
                  {todayAttendance?.check_in && (
                    <span style={payrollUi.clockTimeBadge({ color: '#86efac', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)' })}>
                      {t('clockedInAt')}: {todayAttendance.check_in}
                    </span>
                  )}
                  {todayAttendance?.check_out && (
                    <span style={payrollUi.clockTimeBadge({ color: '#fca5a5', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.35)' })}>
                      {t('clockedOutAt')}: {todayAttendance.check_out}
                    </span>
                  )}
                  {todayAttendance?.check_out && todayAttendance?.hmr && (
                    <span style={payrollUi.clockTimeBadge({ color: '#c9a84c', bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.35)' })}>
                      {t('durationLabel')}: {formatAttendanceHmrDisplay(todayAttendance.hmr)}
                    </span>
                  )}
                </div>
              )}

              {todayAttendance?.owner_proposed_check_in
                && !todayAttendance?.operator_correction_confirmed
                && !todayAttendance?.correction_rejected && (
                <div style={{
                  margin: '0 0 18px',
                  padding: 14,
                  borderRadius: 12,
                  border: '1px solid rgba(201,168,76,0.35)',
                  background: 'rgba(201,168,76,0.08)',
                  textAlign: 'center',
                }}
                >
                  <p style={{ margin: '0 0 10px', color: '#c9a84c', fontSize: 12, fontWeight: 700 }}>
                    Owner proposed check-in correction:
                    {' '}
                    {todayAttendance.owner_proposed_check_in}
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      style={payrollUi.goldActionBtn(correctionSubmitting)}
                      disabled={correctionSubmitting}
                      onClick={() => handleConfirmCorrection(
                        String(todayAttendance.date || '').slice(0, 10)
                          || new Date().toISOString().slice(0, 10),
                        true,
                      )}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      style={{
                        ...payrollUi.goldActionBtn(correctionSubmitting),
                        background: 'rgba(233,69,96,0.15)',
                        border: '1px solid rgba(233,69,96,0.4)',
                        color: '#e94560',
                      }}
                      disabled={correctionSubmitting}
                      onClick={() => handleConfirmCorrection(
                        String(todayAttendance.date || '').slice(0, 10)
                          || new Date().toISOString().slice(0, 10),
                        false,
                      )}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              <p style={{ ...payrollUi.sectionTitle, textAlign: 'left' }}>
                {t('attendanceStatusSection')}
              </p>
              {attendance ? (
                <div style={payrollUi.currentStatusCard(attendanceStatusColor(attendance))}>
                  <p style={payrollUi.currentStatusIcon}>
                    {attendance === 'present' || attendance === 'paid_leave'
                      ? String.fromCodePoint(0x2705)
                      : attendance === 'halfday'
                        ? String.fromCodePoint(0x1F550)
                        : String.fromCodePoint(0x274C)}
                  </p>
                  <p style={payrollUi.currentStatusTitle(attendanceStatusColor(attendance))}>
                    {t('attendanceMarked')}
                  </p>
                  <p style={payrollUi.heroMeta}>
                    {formatAttendanceStatus(attendance, t)}
                  </p>
                </div>
              ) : (
                <p style={{ ...payrollUi.heroMeta, marginBottom: 14, textAlign: 'center' }}>{t('markTodayAttendance')}</p>
              )}
              {plQuota && (
                <p style={{
                  fontSize: 12,
                  margin: '0 0 12px',
                  textAlign: 'center',
                  color: plQuota.pl_remaining === 0 ? '#e94560' : '#8896a8',
                  fontWeight: plQuota.pl_remaining === 0 ? 700 : 500,
                }}
                >
                  PL: {plQuota.pl_used}/{plQuota.annual_pl_quota} {t('plUsed')}
                  {' · '}{t('plRemainingLabel')}: {plQuota.pl_remaining}
                </p>
              )}
              <div style={{
                ...payrollUi.attendanceGrid,
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
                maxWidth: 560,
                margin: '0 auto',
              }}
              >
                {[
                  { type: 'present', icon: String.fromCodePoint(0x2705), label: t('presentLabel'), color: '#4CAF50' },
                  { type: 'halfday', icon: String.fromCodePoint(0x1F550), label: t('halfDayLabel'), color: '#FF9800' },
                  { type: 'paid_leave', icon: String.fromCodePoint(0x1F334), label: t('paidLeaveLabel'), color: '#4CAF50' },
                  { type: 'unpaid_leave', icon: String.fromCodePoint(0x1F6AB), label: t('unpaidLeaveLabel'), color: '#e94560' },
                  { type: 'absent', icon: String.fromCodePoint(0x274C), label: t('absentLabel'), color: '#8896a8' },
                ].map((btn) => (
                  <button
                    key={btn.type}
                    type="button"
                    style={payrollUi.statusChipBtn(btn.color, attendance === btn.type)}
                    onClick={() => handleAttendance(btn.type)}
                  >
                    <span style={{ fontSize: 22, display: 'block', marginBottom: 6 }}>{btn.icon}</span>
                    <span style={payrollUi.attendanceLabel}>{btn.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={payrollUi.historyCard}>
              <p style={payrollUi.sectionTitle}>{t('attendanceHistory')}</p>
              {attendanceRows.length === 0 ? (
                <div style={payrollUi.stateBox}>
                  <p style={{ color: '#8896a8', fontSize: 13, margin: 0 }}>{t('noAttendanceRecords')}</p>
                </div>
              ) : (
              <div style={payrollUi.tableWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                <thead>
                  <tr>
                    {[t('date'), t('status'), t('tableIn'), t('tableOut'), t('hmrCol')].map((h) => (
                      <th key={h} style={payrollUi.tableHeadCell}>{h}</th>
                    ))}
                  </tr>
                </thead>
                  <tbody>
                    {attendanceRows.map((a, i) => (
                      <tr key={a.id || i} style={i % 2 === 0 ? payrollUi.tableRow : payrollUi.tableRowAlt}>
                        <td style={payrollUi.tableCell}>
                          <p style={{ margin: 0, fontWeight: 700, color: '#f0ead8' }}>{a.date}</p>
                          <p style={{ margin: '2px 0 0', color: '#8896a8', fontSize: 10 }}>{a.day}</p>
                        </td>
                        <td style={payrollUi.tableCell}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            color: attendanceStatusColor(a.statusKey),
                            background: `${attendanceStatusColor(a.statusKey)}18`,
                            border: `1px solid ${attendanceStatusColor(a.statusKey)}33`,
                          }}
                          >
                            {formatAttendanceStatus(a.statusKey, t)}
                          </span>
                        </td>
                        <td style={payrollUi.tableCell}>
                          <div>{a.checkIn}</div>
                          {a.correctionPending ? (
                            <div style={{ marginTop: 6 }}>
                              <p style={{ margin: '0 0 6px', color: '#c9a84c', fontSize: 10, fontWeight: 700 }}>
                                Proposed: {a.ownerProposedCheckIn}
                              </p>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  style={{ ...payrollUi.goldActionBtn(correctionSubmitting), fontSize: 10, padding: '4px 8px' }}
                                  disabled={correctionSubmitting || !a.dateKey}
                                  onClick={() => handleConfirmCorrection(a.dateKey, true)}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    ...payrollUi.goldActionBtn(correctionSubmitting),
                                    fontSize: 10,
                                    padding: '4px 8px',
                                    background: 'rgba(233,69,96,0.15)',
                                    border: '1px solid rgba(233,69,96,0.4)',
                                    color: '#e94560',
                                  }}
                                  disabled={correctionSubmitting || !a.dateKey}
                                  onClick={() => handleConfirmCorrection(a.dateKey, false)}
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </td>
                        <td style={payrollUi.tableCell}>{a.checkOut}</td>
                        <td style={{ ...payrollUi.tableCell, color: '#c9a84c', fontWeight: 800 }}>{formatAttendanceHmrDisplay(a.hmr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {attendanceHasMore && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="button"
                    style={payrollUi.loadMoreBtn(attendanceLoadingMore)}
                    disabled={attendanceLoadingMore}
                    onClick={loadMoreAttendance}
                  >
                    {attendanceLoadingMore ? t('loading') : t('loadMoreAttendance')}
                  </button>
                </div>
              )}
              <p style={payrollUi.footerNote}>
                {t('loadedAttendanceRows').replace('{{count}}', attendanceRows.length)}
                {attendanceHasMore ? ` (${t('moreAvailable')})` : ` (${t('allLoaded')})`}
              </p>
            </div>
          </div>
        )}

        {/* --- PAYROLL TAB --- */}
        {activeTab === 'payroll' && (
          <div style={payrollUi.shell}>
            <style>{'@keyframes payrollShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }'}</style>

            <div style={{ ...s.card, padding: '18px 18px 16px' }}>
              <div style={payrollUi.headerRow}>
                <div>
                  <h3 style={payrollUi.headerTitle}>{t('operatorPayrollTitle')}</h3>
                  <p style={payrollUi.headerSub}>
                    {loggedInUser?.name || operatorDisplayName}
                    {' · '}
                    {formatPayrollMonthLabel(payrollMonth)}
                  </p>
                </div>
                <span style={payrollUi.periodBadge}>
                  <CalendarDays size={14} />
                  {payrollMonth}
                </span>
              </div>

              <div style={{ ...payrollUi.monthToggle, marginTop: 14 }}>
                <button
                  type="button"
                  style={payrollUi.monthBtn(payrollMonth === payrollPrevMonth)}
                  onClick={() => setPayrollMonth(payrollPrevMonth)}
                >
                  {t('payrollPreviousMonth')}
                </button>
                <button
                  type="button"
                  style={payrollUi.monthBtn(payrollMonth === payrollCurrentMonth)}
                  onClick={() => setPayrollMonth(payrollCurrentMonth)}
                >
                  {t('payrollCurrentMonth')}
                </button>
              </div>
            </div>

            {payrollLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ ...payrollUi.skeleton, height: 132 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} style={{ ...payrollUi.skeleton, height: 92 }} />
                  ))}
                </div>
              </div>
            )}

            {payrollError && !payrollLoading && (
              <div style={payrollUi.stateBox}>
                <p style={{ color: '#e94560', fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>{payrollError}</p>
                <button type="button" style={{ ...s.cancelBtn, fontSize: 11 }} onClick={loadPayrollPreview}>
                  {t('retryLabel')}
                </button>
              </div>
            )}

            {!payrollLoading && !payrollError && !payrollData && (
              <div style={payrollUi.stateBox}>
                <Wallet size={28} color="#8896a8" style={{ marginBottom: 10 }} />
                <p style={{ color: '#8896a8', fontSize: 13, margin: 0 }}>{t('noRecordsMonth')}</p>
              </div>
            )}

            {payrollData && !payrollLoading && (
              <>
                <div style={payrollUi.heroCard}>
                  <p style={payrollUi.heroLabel}>{t('payrollNet')}</p>
                  <p style={{ ...payrollUi.heroAmount, fontSize: isMobile ? 30 : 36 }}>
                    {formatPayrollCurrency(payrollData.net_payable)}
                  </p>
                  <p style={payrollUi.heroMeta}>
                    {t('dailyPayrollHint')}
                  </p>
                </div>

                <div style={payrollUi.kpiGrid}>
                  {[
                    {
                      icon: IndianRupee,
                      label: t('payrollMonthlySalary'),
                      value: formatPayrollCurrency(payrollData.monthly_salary),
                      accent: { color: '#e2c97e', bg: 'rgba(201,168,76,0.14)', border: 'rgba(201,168,76,0.28)' },
                      valueColor: '#f0ead8',
                    },
                    {
                      icon: TrendingUp,
                      label: t('payrollDailyRate'),
                      value: formatPayrollCurrency(payrollData.daily_rate),
                      accent: { color: '#93c5fd', bg: 'rgba(59,130,246,0.14)', border: 'rgba(59,130,246,0.28)' },
                      valueColor: '#dbeafe',
                    },
                    {
                      icon: Wallet,
                      label: t('payrollGross'),
                      value: formatPayrollCurrency(payrollData.gross_payable),
                      accent: { color: '#86efac', bg: 'rgba(34,197,94,0.14)', border: 'rgba(34,197,94,0.28)' },
                      valueColor: '#bbf7d0',
                    },
                    {
                      icon: ArrowDownCircle,
                      label: t('advanceBalance'),
                      value: formatPayrollCurrency(payrollData.advance_balance),
                      accent: { color: '#fdba74', bg: 'rgba(249,115,22,0.14)', border: 'rgba(249,115,22,0.28)' },
                      valueColor: '#fed7aa',
                    },
                  ].map(({ icon: Icon, label, value, accent, valueColor }) => (
                    <div key={label} style={payrollUi.kpiCard(accent)}>
                      <div style={payrollUi.kpiIcon(accent)}>
                        <Icon size={16} />
                      </div>
                      <p style={payrollUi.kpiLabel}>{label}</p>
                      <p style={payrollUi.kpiValue(valueColor)}>{value}</p>
                    </div>
                  ))}
                </div>

                <div style={{ ...s.card, padding: '16px 16px 14px' }}>
                  <p style={payrollUi.sectionTitle}>{t('attendanceStatusSection')}</p>
                  <div style={payrollUi.attendanceGrid}>
                    {[
                      { key: 'present', label: t('presentLabel'), color: '#4CAF50' },
                      { key: 'halfday', label: t('halfDayLabel'), color: '#FF9800' },
                      { key: 'paid_leave', label: t('paidLeaveLabel'), color: '#22c55e' },
                      { key: 'unpaid_leave', label: t('unpaidLeaveLabel'), color: '#e94560' },
                      { key: 'absent', label: t('absentLabel'), color: '#8896a8' },
                    ].map(({ key, label, color }) => (
                      <div key={key} style={payrollUi.attendanceChip(color)}>
                        <p style={payrollUi.attendanceCount(color)}>{payrollAttendanceSummary[key] ?? 0}</p>
                        <p style={payrollUi.attendanceLabel}>{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  style={{
                    ...payrollUi.goldActionBtn(false),
                    width: '100%',
                    marginTop: 4,
                  }}
                  onClick={() => generatePayslipPDF(
                    payrollData,
                    payrollMonth,
                    payrollData.operator_name || loggedInUser?.name,
                    t,
                  )}
                >
                  {String.fromCodePoint(0x1F4C4)} {t('downloadPayslip')}
                </button>
              </>
            )}
          </div>
        )}

        {/* --- MACHINE LOG TAB --- */}
        {activeTab === 'machinelog' && (
          <div>
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F69C)} {t('liveBookingTitle')} {String.fromCodePoint(0x00B7)} {assignmentMachineLabel}</h3>
              <div style={{ borderRadius: '12px', padding: '16px', textAlign: 'center', marginBottom: '14px', background: `${assignmentStatusColor}14`, border: `2px solid ${assignmentStatusColor}` }}>
                <p style={{ fontSize: '32px', margin: '0 0 6px', fontWeight: 800, color: assignmentStatusColor, fontVariantNumeric: 'tabular-nums' }}>
                  {assignmentRunning && activeAssignment?.machine_started_at
                    ? formatClockHMS(runningElapsedMs(activeAssignment, currentTime.getTime()))
                    : '00:00:00'}
                </p>
                <p style={{ color: assignmentStatusColor, fontSize: '14px', fontWeight: '700', margin: '0 0 4px' }}>
                  {assignmentPaused ? t('timerPaused') : machineRunning ? t('machineRunningAdmin') : (activeAssignment ? t('assignedAwaitingStart') : t('noActiveBooking'))}
                </p>
                {machineStartLabel && (
                  <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{t('startedAtLabel')} {machineStartLabel}</p>
                )}
              </div>
              {pauseTimerControls}
              <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 12px' }}>
                {t('machineLogTimerHint')}
              </p>
              <OperatorAssignmentPanel
                booking={activeAssignment}
                assignmentSite={assignmentSite}
                t={t}
                onRefresh={refreshActiveAssignment}
                loading={assignmentsLoading}
                error={assignmentError}
                showLiveMeter={false}
              />
            </div>
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F4CB)} {t('todayActivityLog')}</h3>
              <p style={{ color: '#556070', fontSize: '10px', margin: '0 0 10px' }}>{t('machineLogSubtitle')}</p>
              {logs.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', padding: '24px 12px', margin: 0 }}>{t('noLogsYet')}</p>
              ) : (
                logs.map((log, i) => {
                  const { icon, color } = logIcon(log.type);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', marginBottom: '6px', borderLeft: `3px solid ${color}` }}>
                      <span style={{ fontSize: '16px' }}>{icon}</span>
                      <span style={{ color: '#556070', fontSize: '10px', width: '60px', flexShrink: 0 }}>{log.time}</span>
                      <span style={{ color: '#e8e0d0', fontSize: '12px', flex: 1 }}>{log.note}</span>
                      <span style={{ color: '#FF9800', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>{String.fromCodePoint(0x26FD)}{fuelDisplay(log.fuel, fuelCapacity)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* --- FUEL TAB --- */}
        {activeTab === 'fuel' && (
          <div>
            <div style={{ ...s.card, marginBottom: '12px' }}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x26FD)} {t('fuelUpdateTitle')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
                <div style={s.fuelGaugeWrap}>
                  <div style={{ ...s.fuelGaugeFill, height: `${fuelCapacityReady ? (fuelCapacity > 0 ? Math.round((fuelLevel / fuelCapacity) * 100) : fuelLevel) : 0}%`, background: `linear-gradient(180deg, ${fuelColor}, ${fuelColor}88)` }} />
                  <span style={s.fuelGaugePct}>{fuelCapacityReady ? fuelDisplay(fuelLevel, fuelCapacity) : '...'}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: fuelColor, fontSize: '36px', fontWeight: '900', margin: '0 0 4px' }}>{fuelCapacityReady ? fuelDisplay(fuelLevel, fuelCapacity) : '...'}</p>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 10px' }}>{t('fuelSensorAccuracyNote')}</p>
                  <input type="range" min="0" max="100" value={fuelLevel} onChange={e => setFuelLevel(Number(e.target.value))} style={{ width: '100%', accentColor: '#c9a84c', marginBottom: '8px' }} />
                  <input style={{ ...s.input, marginBottom: '10px', fontSize: '13px' }} placeholder={t('fuelNotePlaceholder')} value={fuelNote} onChange={e => setFuelNote(e.target.value)} />
                  {fuelSuccess ? (
                    <p style={{ color: '#4CAF50', fontSize: '13px', fontWeight: '700', margin: '0 0 10px' }}>
                      {fuelSuccess}
                    </p>
                  ) : null}
                  <button
                    style={{ ...s.primaryBtn, opacity: fuelSubmitting ? 0.7 : 1, cursor: fuelSubmitting ? 'wait' : 'pointer' }}
                    disabled={fuelSubmitting}
                    onClick={handleFuelUpdate}
                  >
                    {String.fromCodePoint(0x2705)} {t('updateFuel')} {t('submit')}
                  </button>
                </div>
              </div>
            </div>
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x26FD)} {t('fuelHistoryTitle')}</h3>
              {fuelRows.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', padding: '24px 12px', margin: 0 }}>{t('noFuelLogs')}</p>
              ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                <thead><tr>{[t('date'), t('timeLabel'), t('tableLevel'), t('tableChange'), t('tableNote')].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {fuelRows.map((f, i) => (
                      <tr key={i} style={s.tr}>
                        <td style={s.td}>{f.date}</td>
                        <td style={s.td}>{f.time}</td>
                        <td style={s.td}><span style={{ color: f.level > 50 ? '#4CAF50' : '#FF9800', fontWeight: '700' }}>{fuelDisplay(f.level, fuelCapacity)}</span></td>
                        <td style={s.td}><span style={{ color: f.change > 0 ? '#4CAF50' : '#e94560', fontWeight: '700' }}>{f.change > 0 ? '+' : ''}{f.change}%</span></td>
                        <td style={s.td}>{f.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {fuelHasMore && (
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                  <button
                    style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: fuelLoadingMore ? 'wait' : 'pointer', opacity: fuelLoadingMore ? 0.7 : 1 }}
                    disabled={fuelLoadingMore}
                    onClick={loadMoreFuelLogs}
                  >
                    {fuelLoadingMore ? t('loading') : t('loadMoreFuelLogs')}
                  </button>
                </div>
              )}
              <p style={{ color: '#8896a8', fontSize: '11px', textAlign: 'center', margin: '8px 0 0' }}>
                {t('loadedFuelLogs')}: {fuelRows.length}{fuelHasMore ? ` (${t('moreAvailable')})` : ` (${t('allLoaded')})`}
              </p>
            </div>
          </div>
        )}

        {/* --- ALERTS TAB --- */}
        {activeTab === 'alerts' && (
          <div>
            <PushNotificationToggle />
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F514)} {t('allAlerts')}</h3>
              {localAlerts.map((a) => (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => a.link && setActiveTab(a.link)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && a.link) setActiveTab(a.link); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    background: 'rgba(255,152,0,0.08)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${a.color}`,
                    marginBottom: '8px',
                    cursor: a.link ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{a.icon}</span>
                  <div>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px' }}>{a.msg}</p>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{t('live')}</p>
                  </div>
                </div>
              ))}
              {assignmentRunning && activeAssignment ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(76,175,80,0.08)', borderRadius: '8px', borderLeft: '3px solid #4CAF50', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}>{String.fromCodePoint(0x1F7E2)}</span>
                  <div>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px' }}>
                      {assignmentMachineLabel} — machine running ({activeAssignment.booking_ref || '—'})
                    </p>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{t('liveMachineLocation')}</p>
                  </div>
                </div>
              ) : activeAssignment ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(255,152,0,0.08)', borderRadius: '8px', borderLeft: '3px solid #FF9800', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}>{String.fromCodePoint(0x1F69C)}</span>
                  <div>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px' }}>
                      Assigned: {assignmentMachineLabel} ({activeAssignment.booking_ref || '—'})
                    </p>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{t('awaitingAdminStart')}</p>
                  </div>
                </div>
              ) : localAlerts.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('noAlertsNow')}</p>
              ) : null}
            </div>
          </div>
        )}

        {/* --- NOTIFICATIONS TAB --- */}
        {activeTab === 'notifications' && (
          <NotificationCenter
            authToken={localStorage.getItem('developmentexpress_token') || ''}
            apiEndpoint="/api/operator/notifications"
            title={t('notificationCenter')}
          />
        )}

        {/* --- DAILY REPORT TAB --- */}
        {activeTab === 'daily' && (() => {
          const nowReport = new Date();
          const currentReportYear = nowReport.getFullYear();
          const currentReportMonth = nowReport.getMonth() + 1;
          const isCurrentReportMonth = reportYear === currentReportYear && reportMonth === currentReportMonth;
          const reportMonthLabel = new Date(reportYear, reportMonth - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
          const reportRows = reportData?.rows || [];

          const goPrevReportMonth = () => {
            if (reportMonth === 1) {
              setReportMonth(12);
              setReportYear(reportYear - 1);
            } else {
              setReportMonth(reportMonth - 1);
            }
          };

          const goNextReportMonth = () => {
            if (isCurrentReportMonth) return;
            if (reportMonth === 12) {
              setReportMonth(1);
              setReportYear(reportYear + 1);
            } else {
              setReportMonth(reportMonth + 1);
            }
          };

          return (
            <>
              <div style={{ ...s.card, marginBottom: '12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
                  <h3 style={{ color: '#c9a84c', margin: 0, fontSize: '16px', fontWeight: 700 }}>{t('operatorAnalyticsTitle')}</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                      { months: 3, labelKey: 'analytics3Months' },
                      { months: 6, labelKey: 'analytics6Months' },
                      { months: 12, labelKey: 'analytics12Months' },
                    ].map(({ months, labelKey }) => {
                      const active = opAnalyticsMonths === months;
                      return (
                        <button
                          key={months}
                          type="button"
                          onClick={() => setOpAnalyticsMonths(months)}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '8px',
                            border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                            background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                            color: active ? '#c9a84c' : '#8896a8',
                            cursor: 'pointer',
                            fontWeight: active ? 700 : 500,
                            fontSize: '12px',
                          }}
                        >
                          {t(labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {opAnalyticsLoading ? (
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
                ) : opAnalyticsError ? (
                  <div style={{ textAlign: 'center', margin: '16px 0' }}>
                    <p style={{ color: '#e94560', fontSize: '13px', margin: '0 0 10px' }}>{opAnalyticsError}</p>
                    <button
                      type="button"
                      onClick={loadOperatorAnalytics}
                      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      {t('retryLabel')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '8px', marginBottom: '14px' }}>
                      {[
                        { icon: String.fromCodePoint(0x2705), val: operatorAnalytics?.summary?.present_days ?? 0, label: t('operatorAnalyticsPresentDays') },
                        { icon: String.fromCodePoint(0x1F550), val: operatorAnalytics?.summary?.half_days ?? 0, label: t('operatorAnalyticsHalfDays') },
                        { icon: String.fromCodePoint(0x23F1), val: formatHoursHm(operatorAnalytics?.summary?.total_hmr ?? 0), label: t('operatorAnalyticsHmr') },
                        { icon: String.fromCodePoint(0x1F4CB), val: operatorAnalytics?.summary?.jobs_completed ?? 0, label: t('operatorAnalyticsJobs') },
                        { icon: String.fromCodePoint(0x26A0), val: operatorAnalytics?.summary?.issues_count ?? 0, label: t('operatorAnalyticsIssues') },
                        { icon: String.fromCodePoint(0x1F4CA), val: formatHoursHm(operatorAnalytics?.summary?.avg_hmr_per_day ?? 0), label: t('operatorAnalyticsAvgHmr') },
                      ].map((c) => (
                        <div key={c.label} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                          <p style={{ margin: '0 0 4px', fontSize: '18px' }}>{c.icon}</p>
                          <p style={{ color: '#c9a84c', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' }}>{c.val}</p>
                          <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{c.label}</p>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <h4 style={{ color: '#c9a84c', margin: '0 0 12px', fontSize: '14px' }}>{String.fromCodePoint(0x1F4C8)} {t('operatorAnalyticsMonthlyTrend')}</h4>
                      {opAnalyticsTrendSeries.length === 0
                        || (Number(operatorAnalytics?.summary?.present_days || 0) === 0
                          && Number(operatorAnalytics?.summary?.jobs_completed || 0) === 0) ? (
                          <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('operatorAnalyticsNoData')}</p>
                        ) : (
                          <div ref={opChartWrapRef} className="w-full min-w-0" style={{ height: OP_CHART_HEIGHT }}>
                            <ComposedChart
                              width={opChartWidth}
                              height={OP_CHART_HEIGHT}
                              data={opAnalyticsTrendSeries}
                              margin={{ top: 12, right: 12, left: 4, bottom: 4 }}
                            >
                              <defs>
                                <linearGradient id="operatorHmrBarGoldGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={OP_CHART_GOLD_LIGHT} />
                                  <stop offset="100%" stopColor={OP_CHART_GOLD} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid stroke={OP_CHART_GRID} strokeDasharray="4 4" vertical={false} />
                              <XAxis dataKey="month" tick={{ fill: OP_CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: OP_CHART_GRID }} tickLine={false} />
                              <YAxis
                                yAxisId="hmr"
                                tick={{ fill: OP_CHART_AXIS, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatOperatorChartHmr}
                                domain={[0, opAnalyticsMaxHmr * 1.1]}
                              />
                              <YAxis
                                yAxisId="days"
                                orientation="right"
                                allowDecimals={false}
                                tick={{ fill: OP_CHART_AXIS, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <Tooltip
                                content={(
                                  <OperatorAnalyticsTooltip
                                    presentDaysLabel={t('operatorAnalyticsPresentDays')}
                                    jobsLabel={t('operatorAnalyticsJobs')}
                                  />
                                )}
                              />
                              <Bar yAxisId="hmr" dataKey="hmr" fill="url(#operatorHmrBarGoldGrad)" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false} />
                              <Line
                                yAxisId="days"
                                type="monotone"
                                dataKey="present_days"
                                stroke="#4CAF50"
                                strokeWidth={2}
                                dot={{ fill: '#0a1628', stroke: '#4CAF50', strokeWidth: 2, r: 4 }}
                                isAnimationActive={false}
                              />
                            </ComposedChart>
                          </div>
                        )}
                    </div>

                    {Number(operatorAnalytics?.summary?.issues_count || 0) > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <h4 style={{ color: '#c9a84c', margin: '0 0 12px', fontSize: '14px' }}>{String.fromCodePoint(0x26A0)} {t('operatorAnalyticsIssueBreakdown')}</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                          {[
                            { key: 'mechanical', label: t('issueMechanical') },
                            { key: 'fuel', label: t('issueFuelShort') },
                            { key: 'electrical', label: t('issueElectrical') },
                            { key: 'accident', label: t('issueAccident') },
                            { key: 'site', label: t('issueSite') },
                            { key: 'other', label: t('issueOther') },
                          ].map(({ key, label }) => (
                            <div key={key} style={{ background: 'rgba(233,69,96,0.08)', border: '1px solid rgba(233,69,96,0.25)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                              <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{label}</p>
                              <p style={{ color: '#e94560', fontSize: '18px', fontWeight: '700', margin: 0 }}>{operatorAnalytics?.issues_by_type?.[key] ?? 0}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(operatorAnalytics?.recent_jobs || []).length > 0 && (
                      <div>
                        <h4 style={{ color: '#c9a84c', margin: '0 0 12px', fontSize: '14px' }}>{String.fromCodePoint(0x1F69C)} {t('operatorAnalyticsRecentJobs')}</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={s.table}>
                            <thead>
                              <tr>{[t('machine'), t('operatorAnalyticsHmr'), t('date')].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {operatorAnalytics.recent_jobs.map((job, idx) => {
                                const machineLabel = job.machine_id
                                  ? (job.machine_name ? `${job.machine_id} · ${job.machine_name}` : job.machine_id)
                                  : (job.machine_name || '—');
                                return (
                                  <tr key={`${job.machine_id || idx}-${job.date}`} style={s.tr}>
                                    <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{machineLabel}</td>
                                    <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{formatHoursHm(job.hmr)}</td>
                                    <td style={s.td}>{job.date}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F4CB)} {t('dailyWorkReport')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '14px' }}>
                <button
                  type="button"
                  onClick={goPrevReportMonth}
                  style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {String.fromCodePoint(0x2190)}
                </button>
                <span style={{ color: '#e8e0d0', fontSize: '14px', fontWeight: '700', textAlign: 'center' }}>{reportMonthLabel}</span>
                <button
                  type="button"
                  onClick={goNextReportMonth}
                  disabled={isCurrentReportMonth}
                  style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 12px', cursor: isCurrentReportMonth ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: isCurrentReportMonth ? 0.4 : 1 }}
                >
                  {String.fromCodePoint(0x2192)}
                </button>
              </div>

              {reportData?.summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '14px' }}>
                  {[
                    { label: t('presentDaysLabel'), val: reportData.summary.presentDays },
                    { label: t('halfDaysLabel'), val: reportData.summary.halfDays },
                    { label: t('absentDaysLabel'), val: reportData.summary.absentDays },
                    { label: t('totalHmrLabel'), val: `${reportData.summary.totalHmr}h` },
                  ].map((item) => (
                    <div key={item.label} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{item.label}</p>
                      <p style={{ color: '#c9a84c', fontSize: '18px', fontWeight: '700', margin: 0 }}>{item.val}</p>
                    </div>
                  ))}
                </div>
              )}

              {reportLoading && (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>{t('loading')}</p>
              )}

              {!reportLoading && reportError && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <p style={{ color: '#e94560', fontSize: '13px', margin: '0 0 10px' }}>{reportError}</p>
                  <button
                    type="button"
                    onClick={loadDailyReport}
                    style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    {t('retryLabel')}
                  </button>
                </div>
              )}

              {!reportLoading && !reportError && reportRows.length === 0 && (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>{t('noRecordsMonth')}</p>
              )}

              {!reportLoading && !reportError && reportRows.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead><tr>{[t('date'), t('tableAtt'), t('tableStart'), t('tableEnd'), t('hmrToday'), t('fuelStartCol'), t('fuelEndCol'), t('status')].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {reportRows.map((r, i) => {
                        const fuelEndNum = Number(r.fuelEnd);
                        const fuelEndColor = r.fuelEnd === '--' || !Number.isFinite(fuelEndNum)
                          ? '#8896a8'
                          : (fuelEndNum < 30 ? '#e94560' : '#4CAF50');
                        return (
                          <tr key={i} style={s.tr}>
                            <td style={s.td}>{r.date}</td>
                            <td style={s.td}><span style={{ color: r.att === 'P' ? '#4CAF50' : '#FF9800' }}>{r.att === 'P' ? String.fromCodePoint(0x2705) : String.fromCodePoint(0x1F550)}</span></td>
                            <td style={s.td}>{r.start}</td>
                            <td style={s.td}>{r.end}</td>
                            <td style={{ ...s.td, color: '#c9a84c', fontWeight: '700' }}>{formatHoursHm(r.hmr)}</td>
                            <td style={s.td}>{typeof r.fuelStart === 'number' ? fuelDisplay(r.fuelStart, fuelCapacity) : r.fuelStart}</td>
                            <td style={s.td}><span style={{ color: fuelEndColor }}>{typeof r.fuelEnd === 'number' ? fuelDisplay(r.fuelEnd, fuelCapacity) : r.fuelEnd}</span></td>
                            <td style={s.td}><span style={s.successBadge}>{String.fromCodePoint(0x2705)}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>
          );
        })()}

        {/* --- ISSUES TAB --- */}
        {activeTab === 'issues' && (
          <div>
            <div style={{ ...s.card, border: '1px solid rgba(233,69,96,0.3)' }}>
              <h3 style={{ ...s.cardTitle, color: '#e94560' }}>{String.fromCodePoint(0x26A0)} {t('issueReportTitle')} ⚠</h3>
              <p style={{ color: '#8896a8', fontSize: '12px', marginBottom: '14px' }}>{t('issueReportHint')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '14px' }}>
                {buildIssueTypes(t, { includeSiteOther: true }).map(issue => (
                  <button key={issue.id} style={{ borderRadius: '10px', padding: '12px 6px', cursor: 'pointer', textAlign: 'center', border: issueType === issue.id ? '2px solid #e94560' : '1px solid rgba(233,69,96,0.3)', background: issueType === issue.id ? 'rgba(233,69,96,0.15)' : 'rgba(233,69,96,0.05)' }}
                    onClick={() => setIssueType(issue.id)}>
                    <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>{issue.icon}</span>
                    <span style={{ color: issueType === issue.id ? '#e94560' : '#8896a8', fontSize: '10px', fontWeight: '600' }}>{issue.label}</span>
                  </button>
                ))}
              </div>
              <textarea style={{ ...s.input, height: '80px', resize: 'vertical', marginBottom: '12px', fontSize: '13px' }}
                placeholder={t('issueDescribePlaceholder')} value={issueNote} onChange={e => setIssueNote(e.target.value)} />
              <button style={{ ...s.dangerBtn, opacity: (!issueType || !issueNote) ? 0.5 : 1 }}
                disabled={!issueType || !issueNote} onClick={handleReportIssue}>
                {String.fromCodePoint(0x1F6A8)} {t('issueReportSubmit')}
              </button>
            </div>
            <div style={s.card}>
              <h3 style={s.cardTitle}>{String.fromCodePoint(0x1F4CB)} {t('pastIssuesTitle')}</h3>
              {issuesLoading ? (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', padding: '24px 12px' }}>{t('loading')}</p>
              ) : issuesError ? (
                <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                  <p style={{ color: '#e94560', fontSize: '13px', margin: '0 0 10px' }}>{issuesError}</p>
                  <button type="button" style={s.confirmBtn} onClick={loadIssueHistory}>{t('retryLabel')}</button>
                </div>
              ) : issueHistory.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', padding: '24px 12px' }}>{t('noIssuesYet')}</p>
              ) : (
                issueHistory.map((issue) => (
                  <div key={issue.id || issue.created_at} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', marginBottom: '8px', borderLeft: `3px solid ${issue.resolved ? '#4CAF50' : '#e94560'}` }}>
                    <span style={{ fontSize: '16px' }}>{String.fromCodePoint(0x26A0)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#556070', fontSize: '10px', margin: '0 0 2px' }}>{issue.date} {String.fromCodePoint(0x00B7)} [{issue.type}]</p>
                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: '600', margin: '0 0 2px' }}>{issue.note}</p>
                      <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{issue.resolved ? t('issueResolved') : t('underReview')}</p>
                    </div>
                    <span style={{ color: issue.resolved ? '#4CAF50' : '#e94560', fontSize: '16px', flexShrink: 0 }}>
                      {issue.resolved ? String.fromCodePoint(0x2705) : String.fromCodePoint(0x1F504)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* --- KYC TAB --- */}
        {activeTab === 'kyc' && (
          <OperatorKycForm
            authToken={localStorage.getItem('developmentexpress_token') || ''}
            apiBaseUrl={API_BASE_URL}
          />
        )}
      </div>
      </div>

      <PhoneChangeModal
        isOpen={showPhoneChangeModal}
        onClose={() => setShowPhoneChangeModal(false)}
        userRole={loggedInUser.role || 'operator'}
        authToken={localStorage.getItem('developmentexpress_token') || ''}
        onPhoneChanged={handleOperatorPhoneChanged}
      />

      {/* --- FUEL MODAL --- */}
      {showFuelModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '94%' : '360px' }}>
            <h3 style={{ color: '#FF9800', fontSize: '16px', margin: '0 0 14px', textAlign: 'center' }}>{String.fromCodePoint(0x26FD)} {t('fuelLevelUpdate')}</h3>
            <p style={{ color: '#8896a8', textAlign: 'center', marginBottom: '8px' }}>{t('fuelCurrentLabel')} <strong style={{ color: fuelColor }}>{fuelDisplay(fuelLevel, fuelCapacity)}</strong></p>
            <input type="range" min="0" max="100" value={fuelLevel} onChange={e => setFuelLevel(Number(e.target.value))} style={{ width: '100%', accentColor: '#c9a84c', marginBottom: '8px' }} />
            <p style={{ color: '#c9a84c', fontSize: '24px', fontWeight: '700', textAlign: 'center', margin: '0 0 12px' }}>{fuelDisplay(fuelLevel, fuelCapacity)}</p>
            <input style={{ ...s.input, marginBottom: '12px', fontSize: '13px' }} placeholder={t('fuelNoteRefilledPlaceholder')} value={fuelNote} onChange={e => setFuelNote(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={s.cancelBtn} onClick={() => { setFuelLevel(fuelLevelBeforeModal); setFuelNote(''); setShowFuelModal(false); }}>{t('cancelText')}</button>
              <button
                style={{ ...s.confirmBtn, opacity: fuelSubmitting ? 0.7 : 1, cursor: fuelSubmitting ? 'wait' : 'pointer' }}
                disabled={fuelSubmitting}
                onClick={handleFuelUpdate}
              >
                {String.fromCodePoint(0x2705)} {t('updateText')}
              </button>
            </div>
            {fuelSuccess ? (
              <p style={{ color: '#4CAF50', fontSize: '13px', fontWeight: '700', textAlign: 'center', margin: '10px 0 0' }}>
                {fuelSuccess}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* --- ISSUE MODAL --- */}
      {showIssueModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '94%' : '360px' }}>
            <h3 style={{ color: '#e94560', fontSize: '16px', margin: '0 0 14px', textAlign: 'center' }}>{String.fromCodePoint(0x26A0)} {t('quickIssueReport')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {buildIssueTypes(t, { fuelShort: true }).map(issue => (
                <button key={issue.id} style={{ borderRadius: '8px', padding: '10px', cursor: 'pointer', textAlign: 'center', border: issueType === issue.id ? '2px solid #e94560' : '1px solid rgba(233,69,96,0.3)', background: issueType === issue.id ? 'rgba(233,69,96,0.15)' : 'transparent' }}
                  onClick={() => setIssueType(issue.id)}>
                  <span style={{ fontSize: '20px' }}>{issue.icon}</span>
                  <span style={{ color: '#8896a8', fontSize: '11px', display: 'block' }}>{issue.label}</span>
                </button>
              ))}
            </div>
            <textarea style={{ ...s.input, width: '100%', height: '80px', boxSizing: 'border-box', marginBottom: '12px', resize: 'none', fontSize: '13px' }}
              placeholder={t('issueDescribePlaceholder')} value={issueNote} onChange={e => setIssueNote(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={s.cancelBtn} onClick={() => setShowIssueModal(false)}>{t('cancelText')}</button>
              <button style={{ ...s.confirmBtn, background: 'linear-gradient(135deg, #c0392b, #e74c3c)' }} onClick={handleReportIssue}>{String.fromCodePoint(0x1F6A8)} {t('reportText')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { display: 'flex', height: '100svh', maxHeight: '100svh', overflow: 'hidden', background: '#050d1a', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#fff', width: '100%' },
  containerMobile: { position: 'relative', display: 'block', minHeight: '100svh', width: '100%', maxWidth: '100vw', overflowX: 'hidden', background: '#050d1a', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#fff' },
  scrollShell: { position: 'relative', zIndex: 10, display: 'flex', flex: '1 1 0', flexDirection: 'column', minWidth: 0, minHeight: 0, width: '100%', overflow: 'hidden' },
  mainMobile: { display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },

  // Mobile Top Bar
  topBar: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    background: 'linear-gradient(135deg, #0a1628, #0f2040)',
    borderBottom: '1px solid rgba(201,168,76,0.25)',
    padding: 'calc(env(safe-area-inset-top, 0px) + 0px) 14px 0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    height: '56px', boxSizing: 'border-box',
  },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  topLogoCircle: { width: '32px', height: '32px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#0a1628', fontSize: '11px' },
  topTitle: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  topSub: { color: '#8896a8', fontSize: '9px', margin: 0 },
  hamburger: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', borderRadius: '8px', padding: '6px 12px', fontSize: '18px', cursor: 'pointer' },

  // Drawer
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex' },
  drawer: { width: '280px', background: 'linear-gradient(180deg, #0d1f3c, #0a1628)', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '4px 0 24px rgba(0,0,0,0.6)' },
  drawerHeader: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', borderBottom: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.04)', flexShrink: 0 },
  drawerAvatar: { width: '40px', height: '40px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#0a1628', fontSize: '16px', flexShrink: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: '#8896a8', fontSize: '18px', cursor: 'pointer', padding: '4px', flexShrink: 0 },
  drawerMachineStatus: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'rgba(0,0,0,0.3)', flexShrink: 0 },
  drawerNav: { display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', borderRadius: '10px', border: 'none', background: 'transparent', color: '#8896a8', cursor: 'pointer', fontSize: '14px', width: '100%', textAlign: 'left', marginBottom: '2px' },
  drawerNavActive: { display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', borderRadius: '10px', border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer', fontSize: '14px', width: '100%', textAlign: 'left', fontWeight: '700', marginBottom: '2px' },
  drawerLogout: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '13px' },

  // Desktop Sidebar
  sidebar: { position: 'sticky', top: 0, alignSelf: 'flex-start', width: '230px', height: '100dvh', maxHeight: '100dvh', background: 'linear-gradient(180deg, #0f2040, #0a1628)', borderRight: '1px solid rgba(201,168,76,0.2)', padding: '18px 14px', display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0, minHeight: 0, overflow: 'hidden' },
  sidebarHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' },
  sidebarHeaderBrand: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 },
  sidebarToggleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer', flexShrink: 0 },
  sidebarOpenFab: { position: 'fixed', left: 12, top: 'calc(12px + env(safe-area-inset-top, 0px))', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer' },
  sidebarLogo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  logoCircle: { width: '36px', height: '36px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#0a1628', fontSize: '12px', flexShrink: 0 },
  logoTitle: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  logoSub: { color: '#8896a8', fontSize: '10px', margin: 0 },
  divider: { height: '1px', background: 'rgba(201,168,76,0.15)', margin: '8px 0' },
  operatorCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(201,168,76,0.08)', borderRadius: '10px', margin: '4px 0' },
  opAvatar: { width: '36px', height: '36px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a1628', fontWeight: '900', fontSize: '14px', flexShrink: 0 },
  opName: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  opSub: { color: '#8896a8', fontSize: '10px', margin: 0 },
  nav: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#8896a8', cursor: 'pointer', fontSize: '13px', width: '100%', textAlign: 'left' },
  navActive: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.6)', background: 'linear-gradient(to right, rgba(201,168,76,0.25), rgba(201,168,76,0.05))', boxShadow: '0 0 24px rgba(201,168,76,0.2)', color: '#c9a84c', cursor: 'pointer', fontSize: '13px', width: '100%', textAlign: 'left', fontWeight: '700' },
  machineStatusBox: { background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', margin: '4px 0' },
  msLabel: { color: '#8896a8', fontSize: '11px', margin: '3px 0' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '12px', width: '100%', marginTop: 'auto' },
  sidebarFooter: { color: 'rgba(201,168,76,0.4)', fontSize: '9px', textAlign: 'center', marginTop: '8px', letterSpacing: '1px' },

  // Main
  main: { flex: '1 1 0', minWidth: 0, minHeight: 0, overflowY: 'scroll', overflowX: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '14px', borderBottom: '1px solid rgba(201,168,76,0.12)' },
  backBtn: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', flexShrink: 0 },
  pageTitle: { color: '#c9a84c', fontWeight: '700', margin: '0 0 3px' },
  pageDate: { color: '#556070', fontSize: '11px', margin: 0 },
  opBadge: { display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', padding: '8px 14px', borderRadius: '20px', color: '#c9a84c', fontSize: '12px' },
  onlineDot: { width: '7px', height: '7px', background: '#4CAF50', borderRadius: '50%', display: 'inline-block' },

  // Cards
  card: { background: 'linear-gradient(135deg, #0d1f3c, #0a1628)', border: '1px solid rgba(201,168,76,0.18)', borderRadius: '14px', padding: '16px', marginBottom: '12px' },
  cardTitle: { color: '#c9a84c', margin: '0 0 12px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.3px' },

  // Stats
  statCard: { background: 'linear-gradient(135deg, #0d1f3c, #0a1628)', border: '1px solid rgba(201,168,76,0.18)', borderRadius: '14px', padding: '14px 12px', textAlign: 'center' },
  statIconWrap: { width: '40px', height: '40px', background: 'rgba(201,168,76,0.1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' },
  statVal: { fontSize: '18px', fontWeight: '800', margin: '0 0 3px' },
  statLabel: { color: '#556070', fontSize: '10px', margin: 0 },

  // Machine Banner
  machineBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '10px', border: '1px solid', marginBottom: '12px' },

  // Assignment
  assignItem: { background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '12px 10px', textAlign: 'center' },

  // Actions
  actionBtn: { borderRadius: '12px', padding: '14px 6px', cursor: 'pointer', textAlign: 'center', border: '1px solid', transition: 'all 0.2s' },

  // Fuel Gauge
  fuelGaugeWrap: { width: '44px', height: '110px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.2)', display: 'flex', alignItems: 'flex-end', overflow: 'hidden', flexShrink: 0, position: 'relative' },
  fuelGaugeFill: { width: '100%', transition: 'height 0.5s, background 0.3s' },
  fuelGaugePct: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '8px', fontWeight: '700', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)' },
  fuelProgressBg: { height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', marginBottom: '12px' },
  fuelProgressFill: { height: '100%', borderRadius: '3px', transition: 'width 0.5s' },
  fuelUpdateBtn: { background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', padding: '9px 18px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '12px' },

  // Buttons
  primaryBtn: { width: '100%', padding: '12px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '800', cursor: 'pointer' },
  dangerBtn: { width: '100%', padding: '13px', background: 'linear-gradient(135deg, #c0392b, #e74c3c)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '800', cursor: 'pointer' },
  attBtn: { borderRadius: '12px', padding: '18px 8px', cursor: 'pointer', textAlign: 'center', border: '2px solid' },
  successBadge: { background: 'rgba(76,175,80,0.15)', border: '1px solid #4CAF50', color: '#4CAF50', padding: '2px 8px', borderRadius: '20px', fontSize: '10px' },

  // Table
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '400px' },
  th: { padding: '10px 8px', textAlign: 'left', color: 'rgba(201,168,76,0.7)', fontSize: '10px', letterSpacing: '0.8px', borderBottom: '1px solid rgba(201,168,76,0.12)', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '10px 8px', fontSize: '12px', color: '#e8e0d0', whiteSpace: 'nowrap' },

  // Input
  input: { width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box', outline: 'none' },

  // Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px)' },
  modal: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '16px', padding: '22px' },
  cancelBtn: { flex: 1, padding: '11px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#8896a8', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  confirmBtn: { flex: 1, padding: '11px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
};

export default OperatorDashboard;
// updated



